var __merlinGlobal = (typeof window !== 'undefined' ? window : globalThis);
var __merlinTrustedTypes = __merlinGlobal.MerlinTrustedTypes || (__merlinGlobal.MerlinTrustedTypes = (function() {
    function safeSetInnerHTML(element, html) {
        if (!element) return;
        if (!html) {
            element.replaceChildren();
            return;
        }

        var doc = new DOMParser().parseFromString('<body>' + html + '</body>', 'text/html');
        element.replaceChildren();
        Array.from(doc.body.childNodes).forEach(function(node) {
            element.appendChild(document.adoptNode(node));
        });
    }

    function safeInsertAdjacentHTML(element, position, html) {
        if (!element || !html) return;

        var doc = new DOMParser().parseFromString('<body>' + html + '</body>', 'text/html');
        var fragment = document.createDocumentFragment();
        Array.from(doc.body.childNodes).forEach(function(node) {
            fragment.appendChild(document.adoptNode(node));
        });

        switch (position) {
            case 'beforeend':
                element.appendChild(fragment);
                break;
            case 'afterbegin':
                element.prepend(fragment);
                break;
            case 'beforebegin':
                element.before(fragment);
                break;
            case 'afterend':
                element.after(fragment);
                break;
        }
    }

    return {
        safeSetInnerHTML: safeSetInnerHTML,
        safeInsertAdjacentHTML: safeInsertAdjacentHTML
    };
})());
var safeSetInnerHTML = __merlinTrustedTypes.safeSetInnerHTML;
var safeInsertAdjacentHTML = __merlinTrustedTypes.safeInsertAdjacentHTML;
/**
 * Graph Explorer - Floating Windows Module
 * Draggable, resizable mini windows for Find, Aliases, Details, Operators, Legend, Algorithms, and Functions
 */

(function(global) {
    'use strict';

    const windows = {};
    let activeWindow = null;
    let dragState = null;
    let resizeState = null;
    let selectedNodesData = []; // Track multiple selected nodes
    let globalEventsBound = false;  // Prevent duplicate event binding on reinit

    // Tab-to-window mapping: which floating windows belong to which tab
    const tabWindowMap = {
        graph: ['explorer', 'aliases', 'selection', 'patterns'],
        bookmarks: ['algorithms', 'functions', 'documents']
    };

    // Track which windows were open before being hidden by a tab switch
    // e.g. { graph: ['explorer', 'patterns'], bookmarks: ['algorithms'] }
    const hiddenWindowState = {};

    // Window configurations
    const windowConfigs = {
        explorer: {
            title: 'Search & Find',
            icon: '🔎',
            defaultWidth: 380,
            defaultHeight: 500,
            defaultRight: 76,
            defaultTop: 80
        },
        settings: {
            title: 'Settings',
            icon: '⚙',
            defaultWidth: 340,
            defaultHeight: 600,
            defaultRight: 76,
            defaultTop: 60
        },
        aliases: {
            title: 'Aliases',
            icon: '🔗',
            defaultWidth: 320,
            defaultHeight: 350,
            defaultRight: 432,
            defaultTop: 100
        },
        selection: {
            title: 'Selection & Operators',
            icon: 'ℹ',
            defaultWidth: 350,
            defaultHeight: 550,
            defaultRight: 76,
            defaultTop: 60
        },
        legend: {
            title: 'Legend',
            icon: '🎨',
            defaultWidth: 280,
            defaultHeight: 300,
            defaultRight: 76,
            defaultBottom: 20
        },
        algorithms: {
            title: 'Algorithms',
            icon: '🧮',
            defaultWidth: 320,
            defaultHeight: 500,
            defaultRight: 700,
            defaultTop: 60
        },
        functions: {
            title: 'Functions',
            icon: '💡',
            defaultWidth: 340,
            defaultHeight: 480,
            defaultRight: 1040,
            defaultTop: 60
        },
        documents: {
            title: 'Documents',
            icon: '📄',
            defaultWidth: 380,
            defaultHeight: 500,
            defaultRight: 380,
            defaultTop: 80
        },
        fql: {
            title: 'FQL Editor',
            icon: '📝',
            defaultWidth: 450,
            defaultHeight: 520,
            defaultRight: 76,
            defaultTop: 80
        },
        findSelect: {
            title: 'Find & Select',
            icon: '🔍',
            defaultWidth: 380,
            defaultHeight: 560,
            defaultRight: 76,
            defaultTop: 80
        },
        checkpoints: {
            title: 'Checkpoints & Bookmarks',
            icon: '📌',
            defaultWidth: 360,
            defaultHeight: 450,
            defaultRight: 76,
            defaultTop: 80
        },
        patterns: {
            title: 'Patterns',
            icon: '🔷',
            defaultWidth: 400,
            defaultHeight: 500,
            defaultRight: 76,
            defaultTop: 80
        },
        templates: {
            title: 'Templates',
            icon: '📋',
            defaultWidth: 420,
            defaultHeight: 520,
            defaultRight: 76,
            defaultTop: 80
        }
    };

    // Operator definitions
    // requiresSelection: true = only enabled when nodes selected
    // requiresNoSelection: true = only enabled when NO nodes selected
    // Note: Find and Search have their own dedicated panel in the Explorer window
    const operators = [
        { id: 'extend', icon: 'expand', label: 'Extend', description: 'Extend from selection', color: '#888888', requiresSelection: true },
        { id: 'connect', icon: 'git-branch', label: 'Connect', description: 'Find connections', color: '#22c55e', requiresSelection: true },
        { id: 'map', icon: 'map', label: 'Map', description: 'Map to taxonomy', color: '#888888', requiresSelection: true },
        { id: 'intersect', icon: 'layers', label: 'Intersect', description: 'Intersection of selections', color: '#888888', requiresSelection: true },
        { id: 'differs', icon: 'diff', label: 'Differs', description: 'Differences between selections', color: '#ec4899', requiresSelection: true },
        { id: 'union', icon: 'merge', label: 'Union', description: 'Union of selections', color: '#06b6d4', requiresSelection: true },
        { id: 'isolate', icon: 'scissors', label: 'Isolate', description: 'Isolate selection', color: '#f59e0b', requiresSelection: true },
        { id: 'remove', icon: 'trash-2', label: 'Remove', description: 'Remove from graph', color: '#ef4444', requiresSelection: true }
    ];

    // Algorithm definitions by category
    const algorithmCategories = [
        {
            id: 'centrality',
            name: 'Centrality',
            icon: '📊',
            description: 'Find important nodes',
            algorithms: [
                { id: 'pagerank', name: 'PageRank', description: 'Find influential nodes' },
                { id: 'degree', name: 'Degree', description: 'Find hubs (most connections)' },
                { id: 'betweenness', name: 'Betweenness', description: 'Find bridge nodes' },
                { id: 'closeness', name: 'Closeness', description: 'Find central nodes' },
                { id: 'eigenvector', name: 'Eigenvector', description: 'Quality connections' },
                { id: 'harmonic', name: 'Harmonic', description: 'Handles disconnected graphs' }
            ]
        },
        {
            id: 'community',
            name: 'Community',
            icon: '🔷',
            description: 'Detect clusters & groups',
            algorithms: [
                { id: 'louvain', name: 'Louvain', description: 'Detect communities' },
                { id: 'label_propagation', name: 'Label Propagation', description: 'Fast clustering' },
                { id: 'wcc', name: 'WCC', description: 'Weakly connected components' },
                { id: 'scc', name: 'SCC', description: 'Strongly connected components' },
                { id: 'k_core', name: 'K-Core', description: 'Dense subgraph' },
                { id: 'triangle_count', name: 'Triangle Count', description: 'Count triangles' }
            ]
        },
        {
            id: 'path',
            name: 'Path Finding',
            icon: '🛤️',
            description: 'Find paths between nodes',
            algorithms: [
                { id: 'dijkstra', name: 'Dijkstra', description: 'Shortest path' },
                { id: 'bfs', name: 'BFS', description: 'Breadth-first search' },
                { id: 'dfs', name: 'DFS', description: 'Depth-first search' },
                { id: 'astar', name: 'A*', description: 'Heuristic search' },
                { id: 'yen', name: 'Yen K-Paths', description: 'K shortest paths' }
            ]
        },
        {
            id: 'similarity',
            name: 'Similarity',
            icon: '🔗',
            description: 'Find similar nodes',
            algorithms: [
                { id: 'node_similarity', name: 'Node Similarity', description: 'Jaccard similarity' },
                { id: 'knn', name: 'KNN', description: 'K-nearest neighbors' }
            ]
        },
        {
            id: 'link_prediction',
            name: 'Link Prediction',
            icon: '🔮',
            description: 'Predict missing links',
            algorithms: [
                { id: 'common_neighbors', name: 'Common Neighbors', description: 'Basic prediction' },
                { id: 'adamic_adar', name: 'Adamic Adar', description: 'Weighted by rarity' },
                { id: 'preferential_attachment', name: 'Preferential Attachment', description: 'Growth model' }
            ]
        },
        {
            id: 'embedding',
            name: 'Embedding',
            icon: '📐',
            description: 'Generate node vectors',
            algorithms: [
                { id: 'fast_rp', name: 'FastRP', description: 'Fast random projection' },
                { id: 'node2vec', name: 'Node2Vec', description: 'Random walk embeddings' },
                { id: 'graph_sage', name: 'GraphSAGE', description: 'Inductive embeddings' }
            ]
        }
    ];

    // Function definitions - high-level business functions
    const functionCategories = [
        {
            id: 'insights',
            name: 'Insights',
            icon: '💡',
            functions: [
                { 
                    id: 'main_topics', 
                    name: 'Identify Main Topics', 
                    description: 'Find the main themes and ideas in your graph',
                    algo: 'COMMUNITY.LOUVAIN + CENTRALITY.PAGERANK',
                    color: '#888888'
                },
                { 
                    id: 'influential_concepts', 
                    name: 'Influential Concepts', 
                    description: 'What are the most influential nodes?',
                    algo: 'CENTRALITY.PAGERANK + CENTRALITY.EIGENVECTOR',
                    color: '#888888'
                },
                { 
                    id: 'knowledge_hubs', 
                    name: 'Knowledge Hubs', 
                    description: 'Find nodes that connect many others',
                    algo: 'CENTRALITY.DEGREE + CENTRALITY.BETWEENNESS',
                    color: '#22c55e'
                }
            ]
        },
        {
            id: 'gaps',
            name: 'Gap Analysis',
            icon: '🔍',
            functions: [
                { 
                    id: 'topics_to_connect', 
                    name: 'Topics to Connect', 
                    description: 'Generate ideas to bridge structural gaps between clusters',
                    algo: 'COMMUNITY.LOUVAIN + PATH.DIJKSTRA',
                    color: '#f59e0b'
                },
                { 
                    id: 'conceptual_gateways', 
                    name: 'Conceptual Gateways', 
                    description: 'Target nodes to integrate peripheral ideas into the core',
                    algo: 'CENTRALITY.BETWEENNESS + COMMUNITY.WCC',
                    color: '#ec4899'
                },
                { 
                    id: 'orphan_nodes', 
                    name: 'Orphan Nodes', 
                    description: 'Find isolated or poorly connected nodes',
                    algo: 'CENTRALITY.DEGREE + COMMUNITY.WCC',
                    color: '#ef4444'
                }
            ]
        },
        {
            id: 'metrics',
            name: 'Metrics',
            icon: '📈',
            functions: [
                { 
                    id: 'graph_nps', 
                    name: 'Graph Health Score', 
                    description: 'Calculate overall graph connectivity and balance',
                    algo: 'COMMUNITY.MODULARITY + Statistics',
                    color: '#06b6d4'
                },
                { 
                    id: 'cluster_quality', 
                    name: 'Cluster Quality', 
                    description: 'Evaluate the quality of detected communities',
                    algo: 'COMMUNITY.CONDUCTANCE + COMMUNITY.MODULARITY',
                    color: '#888888'
                }
            ]
        },
        {
            id: 'trends',
            name: 'Trends',
            icon: '📊',
            functions: [
                { 
                    id: 'emerging_topics', 
                    name: 'Emerging Topics', 
                    description: 'Identify nodes gaining connections over time',
                    algo: 'CENTRALITY.DEGREE + Time analysis',
                    color: '#22c55e'
                },
                { 
                    id: 'declining_relevance', 
                    name: 'Declining Relevance', 
                    description: 'Find nodes losing centrality',
                    algo: 'CENTRALITY.PAGERANK + Time analysis',
                    color: '#f59e0b'
                }
            ]
        }
    ];

    // ============================================
    // Operator State Management
    // ============================================
    let currentSelectionCount = 0;
    
    /**
     * Update operator button states based on current selection
     * @param {number} selectionCount - Number of selected nodes (optional, uses cached value if not provided)
     */
    function updateOperatorStates(selectionCount) {
        if (typeof selectionCount === 'number') {
            currentSelectionCount = selectionCount;
        }
        
        const hasSelection = currentSelectionCount > 0;
        const operatorBtns = document.querySelectorAll('.operator-btn');
        
        operatorBtns.forEach(btn => {
            const requiresSelection = btn.dataset.requiresSelection === 'true';
            const requiresNoSelection = btn.dataset.requiresNoSelection === 'true';
            
            let isEnabled = true;
            
            if (requiresSelection && !hasSelection) {
                isEnabled = false;
            } else if (requiresNoSelection && hasSelection) {
                isEnabled = false;
            }
            
            btn.classList.toggle('disabled', !isEnabled);
            btn.disabled = !isEnabled;
        });
        
        console.log(`[ExplorerWindows] Updated operator states: ${currentSelectionCount} selected, hasSelection=${hasSelection}`);
    }

    // ============================================
    // Initialization
    // ============================================
    function init() {
        // Reset windows state on reinit (workspace change)
        reset();
        createWindowContainer();
        bindGlobalEvents();
        setupResizeObserver();
        console.log('[ExplorerWindows] Initialized');
        // Apply any color mapping that was computed before windows were ready
        updateLegend();
    }

    /**
     * Reset windows state - called on workspace change
     */
    function reset() {
        // Clear all window references (DOM elements may be stale)
        for (const key in windows) {
            delete windows[key];
        }
        activeWindow = null;
        
        // Remove existing window container if present
        const container = document.getElementById('floating-windows');
        if (container) {
            safeSetInnerHTML(container, '');
        }
    }

    function createWindowContainer() {
        if (!document.getElementById('floating-windows')) {
            const container = document.createElement('div');
            container.id = 'floating-windows';
            document.querySelector('.graph-explorer')?.appendChild(container);
        }
    }
    
    /**
     * Setup ResizeObserver to adjust window positions when container resizes
     */
    function setupResizeObserver() {
        const container = document.querySelector('.graph-explorer');
        if (!container || typeof ResizeObserver === 'undefined') return;
        
        let lastWidth = container.offsetWidth;
        let lastHeight = container.offsetHeight;
        
        const observer = new ResizeObserver((entries) => {
            const entry = entries[0];
            if (!entry) return;
            
            const newWidth = entry.contentRect.width;
            const newHeight = entry.contentRect.height;
            
            // Only reposition if size actually changed significantly
            if (Math.abs(newWidth - lastWidth) > 10 || Math.abs(newHeight - lastHeight) > 10) {
                repositionWindowsOnResize(lastWidth, lastHeight, newWidth, newHeight);
                lastWidth = newWidth;
                lastHeight = newHeight;
            }
        });
        
        observer.observe(container);
    }
    
    /**
     * Reposition windows when container resizes to keep them visible
     */
    function repositionWindowsOnResize(oldWidth, oldHeight, newWidth, newHeight) {
        Object.values(windows).forEach(win => {
            if (!win.classList.contains('visible')) return;
            
            const rect = win.getBoundingClientRect();
            const container = document.querySelector('.graph-explorer');
            if (!container) return;
            
            const containerRect = container.getBoundingClientRect();
            
            // Calculate current relative position
            let currentLeft = rect.left - containerRect.left;
            let currentTop = rect.top - containerRect.top;
            
            // Adjust if window is outside bounds
            const winWidth = rect.width;
            const winHeight = rect.height;
            const minVisible = 50;
            
            // Keep window visible within new container size
            if (currentLeft + winWidth < minVisible) {
                currentLeft = minVisible - winWidth;
            } else if (currentLeft > newWidth - minVisible) {
                currentLeft = newWidth - minVisible;
            }
            
            if (currentTop < 0) {
                currentTop = 0;
            } else if (currentTop > newHeight - minVisible) {
                currentTop = newHeight - minVisible;
            }
            
            // Only update if we made adjustments
            win.style.left = currentLeft + 'px';
            win.style.top = currentTop + 'px';
        });
    }

    // ============================================
    // Window Creation
    // ============================================
    function createWindow(windowId) {
        const config = windowConfigs[windowId];
        if (!config) return null;

        const win = document.createElement('div');
        win.id = `window-${windowId}`;
        win.className = 'floating-window';
        win.dataset.windowId = windowId;
        win.style.width = config.defaultWidth + 'px';
        win.style.height = config.defaultHeight + 'px';
        
        // Position - support bottom positioning for legend
        if (config.defaultBottom !== undefined) {
            win.style.bottom = config.defaultBottom + 'px';
            win.style.right = config.defaultRight + 'px';
        } else {
            win.style.right = config.defaultRight + 'px';
            win.style.top = config.defaultTop + 'px';
        }

        safeSetInnerHTML(win, `
            <div class="floating-window-header" data-drag="true">
                <span class="floating-window-icon">${config.icon}</span>
                <span class="floating-window-title">${config.title}</span>
                <button class="floating-window-close" data-action="close" title="Close">×</button>
            </div>
            <div class="floating-window-content">
                ${renderWindowContent(windowId)}
            </div>
            <div class="floating-window-resize" data-resize="true"></div>
        `);

        // Templates window goes in the Artefacts tab container, others go in floating-windows
        if (windowId === 'templates') {
            const artefactsContainer = document.getElementById('artefacts-windows-container');
            if (artefactsContainer) {
                artefactsContainer.appendChild(win);
            } else {
                console.warn('[ExplorerWindows] Artefacts container not found, cannot show Templates window');
                return null;
            }
        } else {
            document.getElementById('floating-windows')?.appendChild(win);
        }
        windows[windowId] = win;

        // Bind window-specific events
        bindWindowEvents(win);

        return win;
    }

    function renderWindowContent(windowId) {
        switch (windowId) {
            case 'explorer':
                // Merged Search + Find window (stacked layout)
                return `
                    <div class="explorer-window-content">
                        <!-- Search Panel - Add new nodes to graph -->
                        <div class="explorer-search-panel" id="explorer-search-panel">
                            <div class="explorer-panel-header">
                                <span class="explorer-panel-title">Search</span>
                            </div>
                            <div class="window-section search-window-section">
                                <div id="search-window-entity-input" class="search-entity-input-container"></div>
                            </div>
                            <div class="window-section search-actions">
                                <button type="button" class="window-btn primary" id="search-window-btn">
                                    <i data-lucide="search"></i>
                                    <span>Search & Load</span>
                                </button>
                            </div>
                            <div id="search-window-status" class="search-status"></div>
                        </div>
                        
                        <!-- Find Panel - Filter existing nodes in cursor -->
                        <div class="explorer-find-panel" id="explorer-find-panel">
                            <div class="explorer-panel-header">
                                <span class="explorer-panel-title">Find in Graph</span>
                            </div>
                            <div class="window-section">
                                <input type="text" class="window-input" id="find-search" placeholder="Search nodes by name..." autocomplete="off">
                            </div>
                            <div class="window-section">
                                <label class="window-label small">Filter by attribute:</label>
                                <div class="find-filter-group">
                                    <select class="window-select compact" id="find-filter-type">
                                        <option value="all">All attributes</option>
                                        <option value="resource_name">Resource Name</option>
                                        <option value="domain">Domain</option>
                                        <option value="type">Type</option>
                                        <option value="mixin_name">Mixin Name</option>
                                    </select>
                                    <input type="text" class="window-input compact" id="find-filter-value" placeholder="Filter value..." autocomplete="off">
                                </div>
                            </div>
                            <div class="window-hint">Ctrl+Click to select multiple nodes</div>
                            <div class="window-results" id="find-results">
                                <div class="window-empty">All nodes will appear here</div>
                            </div>
                        </div>
                    </div>
                `;
            case 'findSelect':
                // Combined Find in Graph + Selection panel
                return `
                    <div class="find-select-window-content">
                        <!-- Find Panel - Filter existing nodes in cursor -->
                        <div class="explorer-find-panel" id="fsc-find-panel">
                            <div class="explorer-panel-header">
                                <span class="explorer-panel-title">Find in Graph</span>
                            </div>
                            <div class="window-section">
                                <input type="text" class="window-input" id="fsc-find-search" placeholder="Search nodes by name..." autocomplete="off">
                            </div>
                            <div class="window-section">
                                <div class="find-filter-group">
                                    <select class="window-select compact" id="fsc-filter-type">
                                        <option value="all">All attributes</option>
                                        <option value="resource_name">Resource Name</option>
                                        <option value="domain">Domain</option>
                                        <option value="type">Type</option>
                                        <option value="mixin_name">Mixin Name</option>
                                    </select>
                                    <input type="text" class="window-input compact" id="fsc-filter-value" placeholder="Filter value..." autocomplete="off">
                                </div>
                            </div>
                            <div class="window-hint">Ctrl+Click to select multiple nodes</div>
                            <div class="window-results" id="fsc-find-results">
                                <div class="window-empty">All nodes will appear here</div>
                            </div>
                        </div>

                        <!-- Selection summary -->
                        <div class="selection-panel fsc-selection-panel" id="fsc-selection-panel">
                            <div class="explorer-panel-header">
                                <span class="explorer-panel-title">Selection</span>
                            </div>
                            <div class="window-empty" id="fsc-details-empty">
                                <div class="icon">👆</div>
                                <div class="text">Click a node to see details<br><small>Ctrl+Click for multi-select</small></div>
                            </div>
                            <div id="fsc-details-content" style="display: none;">
                                <div class="selection-summary" id="fsc-selection-summary">
                                    <span class="selection-count">0 nodes selected</span>
                                </div>
                                <div class="selected-nodes-list" id="fsc-selected-nodes-list"></div>
                            </div>
                        </div>
                    </div>
                `;
            case 'selection':
                // Merged Selection + Operators window
                return `
                    <div class="selection-window-content">
                        <!-- Selection Panel -->
                        <div class="selection-panel" id="selection-panel">
                            <div class="window-empty" id="details-empty">
                                <div class="icon">👆</div>
                                <div class="text">Click a node to see details<br><small>Ctrl+Click for multi-select</small></div>
                            </div>
                            <div id="details-content" style="display: none;">
                                <div class="selection-summary" id="selection-summary">
                                    <span class="selection-count">0 nodes selected</span>
                                </div>
                                <div class="selected-nodes-list" id="selected-nodes-list"></div>
                            </div>
                        </div>
                        
                        <!-- Operators Panel -->
                        <div class="operators-panel" id="operators-panel">
                            <div class="operators-header">
                                <span class="operators-title">Operators</span>
                            </div>
                            <div class="operators-grid" id="operators-grid">
                                ${operators.map(op => `
                                    <button class="operator-btn ${op.requiresNoSelection ? 'no-selection-op' : 'selection-op'}" 
                                            data-operator="${op.id}" 
                                            data-requires-selection="${op.requiresSelection || false}"
                                            data-requires-no-selection="${op.requiresNoSelection || false}"
                                            style="--op-color: ${op.color}">
                                        <span class="operator-icon"><i data-lucide="${op.icon}"></i></span>
                                        <span class="operator-label">${op.label}</span>
                                        <span class="operator-desc">${op.description}</span>
                                    </button>
                                `).join('')}
                            </div>
                        </div>
                    </div>
                `;
            case 'settings':
                const colorOptions = [
                    { value: 'resource_name', label: 'Resource Name' },
                    { value: 'domain', label: 'Domain' },
                    { value: 'mixin_name', label: 'Mixin Name' },
                    { value: 'taxonomy_name', label: 'Taxonomy Name' },
                    { value: 'community_id', label: 'Community ID' }
                ];
                const state = (typeof GraphExplorer !== 'undefined' && GraphExplorer.getState) ? GraphExplorer.getState() : {};
                const currentColor = state.currentColorAttribute || 'resource_name';
                const currentScale = state.currentSizeScale || 2;
                const nodeLabelsVisible = state.nodeLabelsVisible !== false;
                const edgeLabelsVisible = state.edgeLabelsVisible === true;
                const currentSolver = state.currentSolver || 'forceAtlas2Based';
                const currentLayout = state.currentLayout || 'default';
                const currentHierSort = state.hierarchicalSortMethod || 'hubsize';
                const physicsEnabled = state.physicsEnabled !== false;
                const physicsDefaults = (typeof ExplorerConstants !== 'undefined' && ExplorerConstants.DEFAULT_PHYSICS_OPTIONS) 
                    ? ExplorerConstants.DEFAULT_PHYSICS_OPTIONS 
                    : {};
                const solverDefaults = physicsDefaults[currentSolver] || {};
                return `
                    <div class="settings-group">
                        <div class="settings-group-title">Graph Layout</div>
                        <div class="settings-row">
                            <label class="settings-label">Layout algorithm:</label>
                            <div class="settings-layout-grid" id="settings-layout-grid">
                                <button class="settings-layout-btn${currentLayout === 'default' ? ' active' : ''}" data-layout="default" title="Force-directed layout using physics simulation">
                                    <i data-lucide="share-2"></i>
                                    <span>Force</span>
                                </button>
                                <button class="settings-layout-btn${currentLayout === 'hierarchical-UD' ? ' active' : ''}" data-layout="hierarchical-UD" title="Top to bottom tree layout">
                                    <i data-lucide="git-branch"></i>
                                    <span>Tree ↓</span>
                                </button>
                                <button class="settings-layout-btn${currentLayout === 'hierarchical-LR' ? ' active' : ''}" data-layout="hierarchical-LR" title="Left to right tree layout">
                                    <i data-lucide="git-branch"></i>
                                    <span>Tree →</span>
                                </button>
                                <button class="settings-layout-btn${currentLayout === 'hierarchical-DU' ? ' active' : ''}" data-layout="hierarchical-DU" title="Bottom to top tree layout">
                                    <i data-lucide="git-branch"></i>
                                    <span>Tree ↑</span>
                                </button>
                                <button class="settings-layout-btn${currentLayout === 'radial' ? ' active' : ''}" data-layout="radial" title="Radial layout from center">
                                    <i data-lucide="sun"></i>
                                    <span>Radial</span>
                                </button>
                                <button class="settings-layout-btn${currentLayout === 'circular' ? ' active' : ''}" data-layout="circular" title="Circular ring layout">
                                    <i data-lucide="circle"></i>
                                    <span>Circle</span>
                                </button>
                                <button class="settings-layout-btn${currentLayout === 'spring' ? ' active' : ''}" data-layout="spring" title="Spring layout (server-computed force-directed)">
                                    <i data-lucide="atom"></i>
                                    <span>Spring</span>
                                </button>
                                <button class="settings-layout-btn${currentLayout === 'kamada_kawai' ? ' active' : ''}" data-layout="kamada_kawai" title="Kamada-Kawai layout (minimizes path-length energy)">
                                    <i data-lucide="waypoints"></i>
                                    <span>K-K</span>
                                </button>
                                <button class="settings-layout-btn${currentLayout === 'spectral' ? ' active' : ''}" data-layout="spectral" title="Spectral layout (eigenvalue-based, good for large graphs)">
                                    <i data-lucide="waves"></i>
                                    <span>Spectral</span>
                                </button>
                            </div>
                        </div>
                        <div class="settings-row settings-hierarchical-options" id="settings-hierarchical-options" style="display: ${currentLayout.startsWith('hierarchical') ? 'flex' : 'none'};">
                            <label class="settings-label">Sort method:</label>
                            <select class="settings-select" id="settings-hierarchical-sort">
                                <option value="hubsize" ${currentHierSort === 'hubsize' ? 'selected' : ''}>Hub Size</option>
                                <option value="directed" ${currentHierSort === 'directed' ? 'selected' : ''}>Directed (Edges)</option>
                                <option value="resource_name" ${currentHierSort === 'resource_name' ? 'selected' : ''}>By Resource Type</option>
                            </select>
                        </div>
                        <div class="settings-row settings-hierarchical-options" id="settings-level-separation-row" style="display: ${currentLayout.startsWith('hierarchical') ? 'flex' : 'none'};">
                            <label class="settings-label">Level separation:</label>
                            <div class="settings-slider-row">
                                <input type="range" class="settings-slider" id="settings-level-separation" min="50" max="400" value="150" step="10">
                                <span class="settings-slider-value" id="level-separation-value">150</span>
                            </div>
                        </div>
                    </div>
                    <div class="settings-group">
                        <div class="settings-group-title">Physics Engine</div>
                        <div class="settings-row">
                            <label class="settings-toggle-row">
                                <span class="settings-label">Enable physics</span>
                                <input type="checkbox" class="settings-checkbox" id="settings-physics-enabled" ${physicsEnabled ? 'checked' : ''}>
                                <span class="settings-toggle-switch"></span>
                            </label>
                        </div>
                        <div id="settings-physics-params" style="opacity: ${physicsEnabled ? '1' : '0.5'}; pointer-events: ${physicsEnabled ? 'auto' : 'none'};">
                            <div class="settings-row">
                                <label class="settings-label">Solver:</label>
                                <select class="settings-select" id="settings-physics-solver">
                                    <option value="barnesHut"${currentSolver === 'barnesHut' ? ' selected' : ''}>Barnes-Hut</option>
                                    <option value="forceAtlas2Based"${currentSolver === 'forceAtlas2Based' ? ' selected' : ''}>Force Atlas 2</option>
                                    <option value="repulsion"${currentSolver === 'repulsion' ? ' selected' : ''}>Repulsion</option>
                                </select>
                            </div>
                            <div class="settings-row">
                                <label class="settings-label">Node spacing:</label>
                                <div class="settings-slider-row">
                                    <input type="range" class="settings-slider" id="settings-spring-length" 
                                           min="50" max="500" value="${solverDefaults.springLength || 200}" step="10">
                                    <span class="settings-slider-value" id="spring-length-value">${solverDefaults.springLength || 200}</span>
                                </div>
                            </div>
                            <div class="settings-row">
                                <label class="settings-label">Spring stiffness:</label>
                                <div class="settings-slider-row">
                                    <input type="range" class="settings-slider" id="settings-spring-constant" 
                                           min="0.01" max="0.5" value="${solverDefaults.springConstant || 0.04}" step="0.01">
                                    <span class="settings-slider-value" id="spring-constant-value">${solverDefaults.springConstant || 0.04}</span>
                                </div>
                            </div>
                            <div class="settings-row">
                                <label class="settings-label">Repulsion strength:</label>
                                <div class="settings-slider-row">
                                    <input type="range" class="settings-slider" id="settings-repulsion-strength" 
                                           min="-10000" max="-100" value="${solverDefaults.gravitationalConstant || -2000}" step="100">
                                    <span class="settings-slider-value" id="repulsion-strength-value">${solverDefaults.gravitationalConstant || -2000}</span>
                                </div>
                            </div>
                            <div class="settings-row">
                                <label class="settings-label">Central gravity:</label>
                                <div class="settings-slider-row">
                                    <input type="range" class="settings-slider" id="settings-central-gravity" 
                                           min="0" max="1" value="${solverDefaults.centralGravity || 0.3}" step="0.05">
                                    <span class="settings-slider-value" id="central-gravity-value">${solverDefaults.centralGravity || 0.3}</span>
                                </div>
                            </div>
                            <div class="settings-row">
                                <label class="settings-label">Damping:</label>
                                <div class="settings-slider-row">
                                    <input type="range" class="settings-slider" id="settings-damping" 
                                           min="0.01" max="1" value="${solverDefaults.damping || 0.2}" step="0.01">
                                    <span class="settings-slider-value" id="damping-value">${solverDefaults.damping || 0.2}</span>
                                </div>
                            </div>
                            <div class="settings-row">
                                <div class="settings-actions">
                                    <button class="settings-action-btn" id="settings-physics-reset">Reset Defaults</button>
                                    <button class="settings-action-btn primary" id="settings-physics-stabilize">Stabilize</button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="settings-group">
                        <div class="settings-group-title">Label Visibility</div>
                        <div class="settings-row">
                            <label class="settings-toggle-row">
                                <span class="settings-label">Node labels</span>
                                <input type="checkbox" class="settings-checkbox" id="settings-node-labels" ${nodeLabelsVisible ? 'checked' : ''}>
                                <span class="settings-toggle-switch"></span>
                            </label>
                        </div>
                        <div class="settings-row">
                            <label class="settings-toggle-row">
                                <span class="settings-label">Edge labels</span>
                                <input type="checkbox" class="settings-checkbox" id="settings-edge-labels" ${edgeLabelsVisible ? 'checked' : ''}>
                                <span class="settings-toggle-switch"></span>
                            </label>
                        </div>
                    </div>
                    <div class="settings-group">
                        <div class="settings-group-title">Node Appearance</div>
                        <div class="settings-row">
                            <label class="settings-label">Color by:</label>
                            <select class="settings-select" id="settings-color-select">
                                ${colorOptions.map(opt => `<option value="${opt.value}"${opt.value === currentColor ? ' selected' : ''}>${opt.label}</option>`).join('')}
                            </select>
                        </div>
                        <div class="settings-row">
                            <label class="settings-label">Size by:</label>
                            <select class="settings-select" id="settings-size-by-select">
                                <option value="fixed">Fixed</option>
                                <option value="degree" selected>Connections (Degree)</option>
                                <option value="betweenness">Betweenness Centrality</option>
                                <option value="closeness">Closeness Centrality</option>
                                <option value="pagerank">PageRank</option>
                            </select>
                        </div>
                        <div class="settings-row">
                            <label class="settings-label">Size scale:</label>
                            <div class="settings-btn-group">
                                ${[1, 2, 4, 8].map(s => `<button class="settings-scale-btn${s === currentScale ? ' active' : ''}" data-scale="${s}">x${s}</button>`).join('')}
                            </div>
                        </div>
                    </div>
                    <div class="settings-group">
                        <div class="settings-group-title">Edge Appearance</div>
                        <div class="settings-row">
                            <label class="settings-label">Color by:</label>
                            <select class="settings-select" id="settings-edge-color-select">
                                <option value="default" selected>Default</option>
                                <option value="name">Edge Name</option>
                                <option value="type">Edge Type</option>
                                <option value="weight">Weight</option>
                            </select>
                        </div>
                        <div class="settings-row">
                            <label class="settings-label">Width by:</label>
                            <select class="settings-select" id="settings-edge-width-select">
                                <option value="fixed" selected>Fixed</option>
                                <option value="weight">Weight</option>
                                <option value="count">Connection Count</option>
                            </select>
                        </div>
                    </div>
                    <div class="settings-group">
                        <div class="settings-group-title">Label Font Size</div>
                        <div class="settings-row">
                            <label class="settings-label">Node labels:</label>
                            <div class="settings-slider-row">
                                <input type="range" class="settings-slider" id="settings-node-font" min="8" max="24" value="14">
                                <span class="settings-slider-value" id="node-font-value">14px</span>
                            </div>
                        </div>
                        <div class="settings-row">
                            <label class="settings-label">Edge labels:</label>
                            <div class="settings-slider-row">
                                <input type="range" class="settings-slider" id="settings-edge-font" min="6" max="18" value="10">
                                <span class="settings-slider-value" id="edge-font-value">10px</span>
                            </div>
                        </div>
                    </div>
                    <div class="settings-group">
                        <div class="settings-group-title">API / Performance</div>
                        <div class="settings-row">
                            <label class="settings-label">Max API nodes:</label>
                            <div class="settings-slider-row">
                                <input type="range" class="settings-slider" id="settings-max-nodes" min="500" max="50000" step="500" value="${(typeof ExplorerAPI !== 'undefined' && ExplorerAPI.getMaxNodes) ? ExplorerAPI.getMaxNodes() : 5000}" title="Server-side node budget. Higher = more data from API.">
                                <span class="settings-slider-value" id="max-nodes-value">${(typeof ExplorerAPI !== 'undefined' && ExplorerAPI.getMaxNodes) ? ExplorerAPI.getMaxNodes() : 5000}</span>
                            </div>
                        </div>
                        <div class="settings-row">
                            <label class="settings-label">Max display nodes:</label>
                            <div class="settings-slider-row">
                                <input type="range" class="settings-slider" id="settings-max-display" min="50" max="10000" step="50" value="${(typeof ExplorerClustering !== 'undefined' && ExplorerClustering.maxClusters) ? ExplorerClustering.maxClusters : 500}" title="Client-side display budget. Small types stay individual; large types become clusters.">
                                <span class="settings-slider-value" id="max-display-value">${(typeof ExplorerClustering !== 'undefined' && ExplorerClustering.maxClusters) ? ExplorerClustering.maxClusters : 500}</span>
                            </div>
                        </div>
                    </div>
                    <div class="settings-group">
                        <div class="settings-group-title">Quick Actions</div>
                        <div class="settings-actions">
                            <button class="settings-action-btn" id="settings-reset-colors">Reset Colors</button>
                            <button class="settings-action-btn" id="settings-reset-sizes">Reset Sizes</button>
                        </div>
                    </div>
                `;
            case 'aliases':
                return `
                    <div class="window-section">
                        <label class="window-label">Available Aliases:</label>
                    </div>
                    <div class="window-results" id="aliases-list">
                        <div class="window-empty">No aliases defined</div>
                    </div>
                    <div class="window-section" style="margin-top: auto; padding-top: 12px; border-top: 1px solid var(--explorer-border);">
                        <button class="window-btn primary" id="create-alias-btn">+ Create Alias</button>
                    </div>
                `;
            case 'legend':
                return `
                    <div class="legend-section">
                        <div class="legend-title">Color by: <span id="legend-color-attr">Domain</span></div>
                        <div class="legend-hint">Click to highlight &bull; Double-click to filter</div>
                        <div class="legend-items" id="legend-color-items">
                            <div class="window-empty">No color mapping</div>
                        </div>
                    </div>
                    <div class="legend-section" style="margin-top: 12px; padding-top: 12px; border-top: 1px solid var(--explorer-border);">
                        <div class="legend-title">Edges</div>
                        <div class="legend-hint">Click to highlight &bull; Double-click to filter</div>
                        <div class="legend-items" id="legend-edge-items">
                            <div class="window-empty small">No edges in graph</div>
                        </div>
                    </div>
                    <div class="legend-section" style="margin-top: 12px; padding-top: 12px; border-top: 1px solid var(--explorer-border);">
                        <div class="legend-title">Size by: <span id="legend-size-attr">Default</span></div>
                        <div class="legend-size-scale" id="legend-size-scale">
                            <div class="size-indicator small"></div>
                            <span>Small</span>
                            <div class="size-indicator medium"></div>
                            <span>Medium</span>
                            <div class="size-indicator large"></div>
                            <span>Large</span>
                        </div>
                    </div>
                    <div class="legend-filter-bar" id="legend-filter-bar" style="display:none;">
                        <span class="legend-filter-label" id="legend-filter-desc"></span>
                        <button class="legend-filter-clear" id="legend-filter-clear" title="Clear all filters">✕ Clear filters</button>
                    </div>
                `;
            case 'algorithms':
                return `
                    <div class="algo-categories">
                        ${algorithmCategories.map(cat => `
                            <div class="algo-category" data-category="${cat.id}">
                                <div class="algo-category-header" data-collapse="${cat.id}">
                                    <span class="algo-category-icon">${cat.icon}</span>
                                    <span class="algo-category-name">${cat.name}</span>
                                    <span class="algo-category-desc">${cat.description}</span>
                                    <span class="algo-category-toggle">▼</span>
                                </div>
                                <div class="algo-category-content" id="algo-content-${cat.id}">
                                    ${cat.algorithms.map(algo => `
                                        <button class="algo-item" data-algo="${cat.id}.${algo.id}" title="${algo.description}">
                                            <span class="algo-item-name">${algo.name}</span>
                                            <span class="algo-item-desc">${algo.description}</span>
                                        </button>
                                    `).join('')}
                                </div>
                            </div>
                        `).join('')}
                    </div>
                `;
            case 'functions':
                return `
                    <div class="function-categories">
                        ${functionCategories.map(cat => `
                            <div class="function-category">
                                <div class="function-category-header">
                                    <span class="function-category-icon">${cat.icon}</span>
                                    <span class="function-category-name">${cat.name}</span>
                                </div>
                                <div class="function-list">
                                    ${cat.functions.map(fn => `
                                        <button class="function-btn" data-function="${fn.id}" style="--fn-color: ${fn.color}">
                                            <div class="function-info">
                                                <span class="function-name">${fn.name}</span>
                                                <span class="function-desc">${fn.description}</span>
                                            </div>
                                            <span class="function-algo">${fn.algo}</span>
                                        </button>
                                    `).join('')}
                                </div>
                            </div>
                        `).join('')}
                    </div>
                `;
            case 'documents':
                return `
                    <div class="window-section">
                        <input type="text" class="window-input" id="documents-search" placeholder="Search documents..." autocomplete="off">
                    </div>
                    <div class="documents-toolbar">
                        <button class="documents-filter-btn active" data-filter="all">All</button>
                        <button class="documents-filter-btn" data-filter="linked">Linked</button>
                        <button class="documents-filter-btn" data-filter="unlinked">Unlinked</button>
                    </div>
                    <div class="window-results documents-list" id="documents-list">
                        <div class="window-empty">
                            <div class="icon">📄</div>
                            <div class="text">No documents loaded</div>
                            <button class="window-btn primary" id="load-documents-btn" style="margin-top: 12px;">Load Documents</button>
                        </div>
                    </div>
                    <div class="documents-footer" id="documents-footer" style="display: none;">
                        <span class="documents-count">0 documents</span>
                        <button class="window-btn small" id="link-selected-btn">Link to Selection</button>
                    </div>
                `;
            case 'fql':
                return `
                    <div class="fql-editor-section">
                        <label class="window-label">FQL Query:</label>
                        <div class="fql-editor-container">
                            <div class="fql-editor" id="fql-editor" contenteditable="true" spellcheck="false" data-placeholder="Enter FQL query..."></div>
                        </div>
                    </div>
                    <div class="fql-actions">
                        <button class="window-btn primary" id="fql-execute-btn">
                            <span class="btn-icon">▶</span> Execute
                        </button>
                        <button class="window-btn" id="fql-clear-btn">Clear</button>
                    </div>
                    <div class="fql-history-section">
                        <label class="window-label">History <span class="fql-hint">(click to reuse)</span>:</label>
                        <div class="fql-history-list" id="fql-history-list">
                            <div class="window-empty">No statements yet</div>
                        </div>
                    </div>
                    <div class="fql-result-section" id="fql-result-section" style="display: none;">
                        <label class="window-label">Result:</label>
                        <div class="fql-result" id="fql-result"></div>
                    </div>
                `;
            case 'checkpoints':
                return `
                    <div class="checkpoints-tabs">
                        <button class="checkpoints-tab active" data-tab="checkpoints">
                            <i data-lucide="flag"></i> Checkpoints
                        </button>
                        <button class="checkpoints-tab" data-tab="bookmarks">
                            <i data-lucide="bookmark"></i> Bookmarks
                        </button>
                    </div>
                    
                    <div class="checkpoints-content" id="checkpoints-tab-content" data-active-tab="checkpoints">
                        <!-- Checkpoints Tab -->
                        <div class="checkpoints-panel" data-panel="checkpoints">
                            <div class="checkpoint-create-section">
                                <div class="checkpoint-create-row">
                                    <input type="text" class="window-input" id="checkpoint-name-input" placeholder="Checkpoint name..." autocomplete="off">
                                    <button class="window-btn primary compact" id="checkpoint-create-btn" title="Create checkpoint">
                                        <i data-lucide="plus"></i>
                                    </button>
                                </div>
                                <div class="checkpoint-hint">Save current graph state as a checkpoint</div>
                            </div>
                            <div class="checkpoints-list" id="checkpoints-list">
                                <div class="window-empty">
                                    <i data-lucide="flag"></i>
                                    <span>No checkpoints yet</span>
                                </div>
                            </div>
                        </div>
                        
                        <!-- Bookmarks Tab -->
                        <div class="checkpoints-panel" data-panel="bookmarks" style="display: none;">
                            <div class="checkpoint-create-section">
                                <div class="checkpoint-create-row">
                                    <input type="text" class="window-input" id="bookmark-name-input" placeholder="Bookmark name..." autocomplete="off">
                                    <button class="window-btn primary compact" id="bookmark-create-btn" title="Create bookmark">
                                        <i data-lucide="plus"></i>
                                    </button>
                                </div>
                                <div class="checkpoint-hint">Create a reusable bookmark from current state</div>
                            </div>
                            <div class="checkpoints-list" id="bookmarks-list">
                                <div class="window-empty">
                                    <i data-lucide="bookmark"></i>
                                    <span>No bookmarks yet</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div id="checkpoint-status" class="checkpoint-status"></div>
                `;
            case 'patterns':
                return `
                    <div class="patterns-window-content">
                        <div class="patterns-header">
                            <div class="patterns-search">
                                <input type="text" class="window-input" id="patterns-search-input" placeholder="Search patterns..." autocomplete="off">
                            </div>
                        </div>
                        <div class="patterns-list" id="patterns-list">
                            <div class="window-loading">
                                <i data-lucide="loader-2" class="spin"></i>
                                <span>Loading patterns...</span>
                            </div>
                        </div>
                        <div id="patterns-status" class="patterns-status"></div>
                    </div>
                `;
            case 'templates':
                return `
                    <div class="templates-window-content">
                        <div class="templates-header">
                            <div class="templates-search">
                                <input type="text" class="window-input" id="templates-search-input" placeholder="Search templates..." autocomplete="off">
                            </div>
                            <div class="templates-filter-row" id="templates-filter-row">
                                <!-- Category filter buttons will be rendered here -->
                            </div>
                        </div>
                        <div class="templates-list" id="templates-list">
                            <div class="window-loading">
                                <i data-lucide="loader-2" class="spin"></i>
                                <span>Loading templates...</span>
                            </div>
                        </div>
                    </div>
                `;
            default:
                return '<div class="window-empty">No content</div>';
        }
    }

    // ============================================
    // Window Events
    // ============================================
    function bindWindowEvents(win) {
        // Close button
        win.querySelector('.floating-window-close')?.addEventListener('click', function() {
            const windowId = win.dataset.windowId;
            close(windowId);
        });

        // Bring to front on click
        win.addEventListener('mousedown', function() {
            bringToFront(win.dataset.windowId);
        });

        // Explorer window handling (merged Search + Find - stacked layout)
        if (win.dataset.windowId === 'explorer') {
            // Find input handling (within explorer window)
            const searchInput = win.querySelector('#find-search');
            const filterType = win.querySelector('#find-filter-type');
            const filterValue = win.querySelector('#find-filter-value');
            
            searchInput?.addEventListener('input', debounce(handleFindInput, 200));
            filterType?.addEventListener('change', handleFindFilterChange);
            filterValue?.addEventListener('input', debounce(handleFindFilterChange, 200));
            
            // Initialize find results on window open
            if (searchInput) {
                handleFindInput({ target: searchInput });
            }
            
            // Initialize search entity input
            initSearchWindowEntityInput(win);
            
            // Initialize Lucide icons
            if (typeof lucide !== 'undefined') {
                setTimeout(() => lucide.createIcons(), 10);
            }
        }

        // Selection window handling (merged Selection + Operators)
        if (win.dataset.windowId === 'selection') {
            // Operators handling
            win.querySelectorAll('.operator-btn').forEach(btn => {
                btn.addEventListener('click', function() {
                    const op = this.dataset.operator;
                    if (typeof ExplorerModal !== 'undefined') {
                        ExplorerModal.open(op);
                    }
                });
            });
            
            // Initialize Lucide icons
            if (typeof lucide !== 'undefined') {
                setTimeout(() => lucide.createIcons(), 10);
            }
        }

        // Find & Select combined window
        if (win.dataset.windowId === 'findSelect') {
            const fscSearch = win.querySelector('#fsc-find-search');
            const fscFilterType = win.querySelector('#fsc-filter-type');
            const fscFilterValue = win.querySelector('#fsc-filter-value');

            const runFscSearch = debounce(function() {
                const query = (fscSearch?.value || '').toLowerCase().trim();
                const ft = fscFilterType?.value || 'all';
                const fv = (fscFilterValue?.value || '').toLowerCase().trim();
                const resultsContainer = win.querySelector('#fsc-find-results');
                if (!resultsContainer) return;
                const nodes = GraphExplorer.getState().nodes;
                if (!nodes) return;
                let matches = [];
                nodes.forEach(function(node) {
                    const label = (node.label || node.id || '').toLowerCase();
                    if (query && !label.includes(query)) return;
                    if (fv) {
                        const attrVal = (node[ft] || '').toString().toLowerCase();
                        if (!attrVal.includes(fv)) return;
                    }
                    matches.push(node);
                });
                if (matches.length === 0 && (query || fv)) {
                    safeSetInnerHTML(resultsContainer, '<div class="window-empty">No nodes found</div>');
                    return;
                }
                matches.sort((a, b) => (a.label || a.id).localeCompare(b.label || b.id));
                const display = matches.slice(0, 100);
                safeSetInnerHTML(resultsContainer, display.map(function(node) {
                    return `<div class="find-result-item" data-node-id="${node.id}">
                        <span class="find-result-name">${node.label || node.id}</span>
                        <span class="find-result-type">${node.resource_name || node.type || ''}</span>
                    </div>`;
                }).join(''));
                if (matches.length > 100) {
                    safeInsertAdjacentHTML(resultsContainer, 'beforeend', `<div class="find-more">+${matches.length - 100} more (refine search)</div>`);
                }
                let fscSelected = [];
                resultsContainer.querySelectorAll('.find-result-item').forEach(function(item) {
                    item.addEventListener('click', function(e) {
                        const nodeId = this.dataset.nodeId;
                        if (e.ctrlKey || e.metaKey) {
                            const idx = fscSelected.indexOf(nodeId);
                            if (idx >= 0) { fscSelected.splice(idx, 1); this.classList.remove('selected'); }
                            else { fscSelected.push(nodeId); this.classList.add('selected'); }
                            if (fscSelected.length > 0) {
                                GraphExplorer.getState().network.selectNodes(fscSelected);
                                GraphExplorer.getState().selectedNodes = fscSelected;
                                GraphExplorer.getState().selectedAlias = null;
                                if (typeof GraphExplorer.highlightMultipleNodes === 'function') GraphExplorer.highlightMultipleNodes(fscSelected);
                                showNodeDetails(fscSelected.map(id => GraphExplorer.getState().nodes.get(id)));
                            }
                        } else {
                            fscSelected = [nodeId];
                            resultsContainer.querySelectorAll('.find-result-item').forEach(el => el.classList.remove('selected'));
                            this.classList.add('selected');
                            GraphExplorer.focusNode(nodeId);
                            GraphExplorer.selectNode(nodeId);
                            showNodeDetails([GraphExplorer.getState().nodes.get(nodeId)]);
                        }
                        // Sync selection details to fsc panel
                        updateFscSelectionPanel(win);
                    });
                });
            }, 200);

            fscSearch?.addEventListener('input', runFscSearch);
            fscFilterType?.addEventListener('change', runFscSearch);
            fscFilterValue?.addEventListener('input', runFscSearch);
            runFscSearch();

            if (typeof lucide !== 'undefined') {
                setTimeout(() => lucide.createIcons(), 10);
            }
        }

        // Legacy Find input handling (for backward compatibility)
        if (win.dataset.windowId === 'find') {
            const searchInput = win.querySelector('#find-search');
            const filterType = win.querySelector('#find-filter-type');
            const filterValue = win.querySelector('#find-filter-value');
            
            searchInput?.addEventListener('input', debounce(handleFindInput, 200));
            filterType?.addEventListener('change', handleFindFilterChange);
            filterValue?.addEventListener('input', debounce(handleFindFilterChange, 200));
            
            // Initial load: show all nodes
            if (searchInput) {
                handleFindInput({ target: searchInput });
            }
        }

        // Legacy Operators handling (for backward compatibility)
        if (win.dataset.windowId === 'operators') {
            win.querySelectorAll('.operator-btn').forEach(btn => {
                btn.addEventListener('click', function() {
                    const op = this.dataset.operator;
                    if (typeof ExplorerModal !== 'undefined') {
                        ExplorerModal.open(op);
                    }
                });
            });
        }

        // Algorithms handling - collapsible categories
        if (win.dataset.windowId === 'algorithms') {
            win.querySelectorAll('.algo-category-header').forEach(header => {
                header.addEventListener('click', function() {
                    const categoryId = this.dataset.collapse;
                    const content = document.getElementById('algo-content-' + categoryId);
                    const category = this.closest('.algo-category');
                    if (content && category) {
                        category.classList.toggle('collapsed');
                    }
                });
            });

            win.querySelectorAll('.algo-item').forEach(btn => {
                btn.addEventListener('click', function() {
                    const algo = this.dataset.algo;
                    handleAlgorithmClick(algo);
                });
            });
        }

        // Functions handling
        if (win.dataset.windowId === 'functions') {
            win.querySelectorAll('.function-btn').forEach(btn => {
                btn.addEventListener('click', function() {
                    const fn = this.dataset.function;
                    handleFunctionClick(fn);
                });
            });
        }

        // Settings handling
        if (win.dataset.windowId === 'settings') {
            // --- Layout buttons ---
            win.querySelectorAll('.settings-layout-btn').forEach(btn => {
                btn.addEventListener('click', function() {
                    const layout = this.dataset.layout;
                    win.querySelectorAll('.settings-layout-btn').forEach(b => b.classList.remove('active'));
                    this.classList.add('active');
                    handleLayoutChange(layout, win);
                });
            });

            // Hierarchical sort method
            const hierarchicalSort = win.querySelector('#settings-hierarchical-sort');
            hierarchicalSort?.addEventListener('change', function() {
                handleHierarchicalSortChange(this.value);
            });

            // Level separation slider
            const levelSepSlider = win.querySelector('#settings-level-separation');
            levelSepSlider?.addEventListener('input', function() {
                const value = parseInt(this.value);
                document.getElementById('level-separation-value').textContent = value;
                handleLevelSeparationChange(value);
            });

            // --- Physics toggle ---
            const physicsToggle = win.querySelector('#settings-physics-enabled');
            physicsToggle?.addEventListener('change', function() {
                handlePhysicsToggle(this.checked, win);
            });

            // Physics solver select
            const solverSelect = win.querySelector('#settings-physics-solver');
            solverSelect?.addEventListener('change', function() {
                handleSolverChange(this.value, win);
            });

            // Physics parameter sliders
            const springLengthSlider = win.querySelector('#settings-spring-length');
            springLengthSlider?.addEventListener('input', function() {
                const value = parseInt(this.value);
                document.getElementById('spring-length-value').textContent = value;
                handlePhysicsParamChange('springLength', value);
            });

            const springConstantSlider = win.querySelector('#settings-spring-constant');
            springConstantSlider?.addEventListener('input', function() {
                const value = parseFloat(this.value);
                document.getElementById('spring-constant-value').textContent = value;
                handlePhysicsParamChange('springConstant', value);
            });

            const repulsionSlider = win.querySelector('#settings-repulsion-strength');
            repulsionSlider?.addEventListener('input', function() {
                const value = parseInt(this.value);
                document.getElementById('repulsion-strength-value').textContent = value;
                handlePhysicsParamChange('gravitationalConstant', value);
            });

            const centralGravitySlider = win.querySelector('#settings-central-gravity');
            centralGravitySlider?.addEventListener('input', function() {
                const value = parseFloat(this.value);
                document.getElementById('central-gravity-value').textContent = value;
                handlePhysicsParamChange('centralGravity', value);
            });

            const dampingSlider = win.querySelector('#settings-damping');
            dampingSlider?.addEventListener('input', function() {
                const value = parseFloat(this.value);
                document.getElementById('damping-value').textContent = value;
                handlePhysicsParamChange('damping', value);
            });

            // Physics reset defaults
            win.querySelector('#settings-physics-reset')?.addEventListener('click', function() {
                handlePhysicsResetDefaults(win);
            });

            // Physics stabilize
            win.querySelector('#settings-physics-stabilize')?.addEventListener('click', function() {
                handlePhysicsStabilize();
            });

            // Node labels toggle
            const nodeLabelsToggle = win.querySelector('#settings-node-labels');
            nodeLabelsToggle?.addEventListener('change', function() {
                handleSettingsNodeLabelsToggle(this.checked);
            });

            // Edge labels toggle
            const edgeLabelsToggle = win.querySelector('#settings-edge-labels');
            edgeLabelsToggle?.addEventListener('change', function() {
                handleSettingsEdgeLabelsToggle(this.checked);
            });

            // Max API nodes slider
            const maxNodesInput = win.querySelector('#settings-max-nodes');
            maxNodesInput?.addEventListener('input', function() {
                var val = parseInt(this.value, 10) || 5000;
                var label = win.querySelector('#max-nodes-value');
                if (label) label.textContent = val;
            });
            maxNodesInput?.addEventListener('change', function() {
                var val = parseInt(this.value, 10) || 5000;
                if (typeof ExplorerAPI !== 'undefined' && ExplorerAPI.setMaxNodes) {
                    ExplorerAPI.setMaxNodes(val);
                }
                console.log('[ExplorerWindows] Max API nodes set to:', val);
            });

            // Max display nodes slider
            const maxDisplayInput = win.querySelector('#settings-max-display');
            maxDisplayInput?.addEventListener('input', function() {
                var val = parseInt(this.value, 10) || 500;
                var label = win.querySelector('#max-display-value');
                if (label) label.textContent = val;
            });
            maxDisplayInput?.addEventListener('change', function() {
                var val = parseInt(this.value, 10) || 500;
                if (typeof ExplorerClustering !== 'undefined' && ExplorerClustering.setMaxClusters) {
                    ExplorerClustering.setMaxClusters(val);
                    if (ExplorerClustering.reclusterCurrentGraph) {
                        ExplorerClustering.reclusterCurrentGraph();
                    }
                }
                console.log('[ExplorerWindows] Max display nodes set to:', val);
            });

            // Color by select
            const colorSelect = win.querySelector('#settings-color-select');
            colorSelect?.addEventListener('change', function() {
                handleSettingsColorChange(this.value);
            });

            // Size by select
            const sizeBySelect = win.querySelector('#settings-size-by-select');
            sizeBySelect?.addEventListener('change', function() {
                handleSettingsSizeByChange(this.value);
            });

            // Size scale buttons
            win.querySelectorAll('.settings-scale-btn').forEach(btn => {
                btn.addEventListener('click', function() {
                    const scale = parseInt(this.dataset.scale);
                    handleSettingsSizeChange(scale, win);
                });
            });

            // Edge color select
            const edgeColorSelect = win.querySelector('#settings-edge-color-select');
            edgeColorSelect?.addEventListener('change', function() {
                handleEdgeColorChange(this.value);
            });

            // Edge width select
            const edgeWidthSelect = win.querySelector('#settings-edge-width-select');
            edgeWidthSelect?.addEventListener('change', function() {
                handleEdgeWidthChange(this.value);
            });

            // Node font size slider
            const nodeFontSlider = win.querySelector('#settings-node-font');
            nodeFontSlider?.addEventListener('input', function() {
                const value = parseInt(this.value);
                document.getElementById('node-font-value').textContent = value + 'px';
                handleNodeFontChange(value);
            });

            // Edge font size slider
            const edgeFontSlider = win.querySelector('#settings-edge-font');
            edgeFontSlider?.addEventListener('input', function() {
                const value = parseInt(this.value);
                document.getElementById('edge-font-value').textContent = value + 'px';
                handleEdgeFontChange(value);
            });

            // Reset buttons
            win.querySelector('#settings-reset-colors')?.addEventListener('click', function() {
                handleSettingsResetColors();
            });
            win.querySelector('#settings-reset-sizes')?.addEventListener('click', function() {
                handleSettingsResetSizes(win);
            });

            // Initialize Lucide icons for layout buttons
            if (typeof lucide !== 'undefined') {
                setTimeout(() => lucide.createIcons(), 10);
            }
        }

        // Documents handling
        if (win.dataset.windowId === 'documents') {
            const searchInput = win.querySelector('#documents-search');
            searchInput?.addEventListener('input', debounce(handleDocumentsSearch, 200));

            win.querySelectorAll('.documents-filter-btn').forEach(btn => {
                btn.addEventListener('click', function() {
                    win.querySelectorAll('.documents-filter-btn').forEach(b => b.classList.remove('active'));
                    this.classList.add('active');
                    handleDocumentsFilter(this.dataset.filter);
                });
            });

            win.querySelector('#load-documents-btn')?.addEventListener('click', handleLoadDocuments);
            win.querySelector('#link-selected-btn')?.addEventListener('click', handleLinkDocuments);
        }

        // FQL Editor handling
        if (win.dataset.windowId === 'fql') {
            const editor = win.querySelector('#fql-editor');
            const executeBtn = win.querySelector('#fql-execute-btn');
            const clearBtn = win.querySelector('#fql-clear-btn');

            // Execute button click
            executeBtn?.addEventListener('click', handleFqlExecute);

            // Clear button click
            clearBtn?.addEventListener('click', function() {
                if (editor) {
                    safeSetInnerHTML(editor, '');
                    editor.focus();
                }
                hideFqlResult();
            });

            // Ctrl+Enter to execute
            editor?.addEventListener('keydown', function(e) {
                if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                    e.preventDefault();
                    handleFqlExecute();
                }
                // Tab key inserts spaces instead of moving focus
                if (e.key === 'Tab') {
                    e.preventDefault();
                    document.execCommand('insertText', false, '  ');
                }
            });

            // Syntax highlighting disabled - use plain text
            // editor?.addEventListener('input', function() {
            //     highlightFqlEditor(this);
            // });

            // Handle paste - strip formatting
            editor?.addEventListener('paste', function(e) {
                e.preventDefault();
                let text = (e.clipboardData || window.clipboardData).getData('text/plain');
                document.execCommand('insertText', false, text);
            });

            // Initial cleanup of editor content (remove any HTML artifacts)
            if (editor && editor.textContent) {
                const text = editor.textContent || '';
                if (text.includes('<span') || text.includes('</span>')) {
                    // Content has HTML artifacts as text - clean it
                    const cleanText = text
                        .replace(/<span[^>]*>/gi, '')
                        .replace(/<\/span>/gi, '')
                        .replace(/<br\s*\/?>/gi, '\n');
                    editor.textContent = cleanText;
                }
            }

            // Initial load of history
            loadFqlHistory();
        }

        // Search window handling - EntityInput integration
        if (win.dataset.windowId === 'search') {
            initSearchWindowEntityInput(win);
        }

        // Checkpoints & Bookmarks window handling
        if (win.dataset.windowId === 'checkpoints') {
            initCheckpointsWindow(win);
        }
        
        // Patterns window handling
        if (win.dataset.windowId === 'patterns') {
            initPatternsWindow(win);
        }
        
        // Templates window handling
        if (win.dataset.windowId === 'templates') {
            initTemplatesWindow(win);
        }
    }

    function bindGlobalEvents() {
        // Prevent duplicate event binding on reinit
        if (globalEventsBound) {
            return;
        }
        globalEventsBound = true;
        
        // Drag handling
        document.addEventListener('mousedown', function(e) {
            const dragHandle = e.target.closest('[data-drag="true"]');
            if (dragHandle) {
                const win = dragHandle.closest('.floating-window');
                if (win) {
                    startDrag(e, win);
                }
            }

            const resizeHandle = e.target.closest('[data-resize="true"]');
            if (resizeHandle) {
                const win = resizeHandle.closest('.floating-window');
                if (win) {
                    startResize(e, win);
                }
            }
        });

        document.addEventListener('mousemove', function(e) {
            if (dragState) {
                handleDrag(e);
            }
            if (resizeState) {
                handleResize(e);
            }
        });

        document.addEventListener('mouseup', function() {
            dragState = null;
            resizeState = null;
        });

        // Collapsible node details
        document.addEventListener('click', function(e) {
            const header = e.target.closest('.node-item-header');
            if (header) {
                const item = header.closest('.node-item');
                if (item) {
                    item.classList.toggle('expanded');
                }
            }
        });
    }

    // ============================================
    // Drag & Resize
    // ============================================
    function startDrag(e, win) {
        const container = document.querySelector('.graph-explorer');
        if (!container) return;
        
        const containerRect = container.getBoundingClientRect();
        const winRect = win.getBoundingClientRect();
        
        // Calculate position relative to container
        const relativeLeft = winRect.left - containerRect.left;
        const relativeTop = winRect.top - containerRect.top;
        
        dragState = {
            win: win,
            container: container,
            containerRect: containerRect,
            startX: e.clientX,
            startY: e.clientY,
            startLeft: relativeLeft,
            startTop: relativeTop
        };
        
        // Switch to left/top positioning (relative to container)
        win.style.right = 'auto';
        win.style.bottom = 'auto';
        win.style.left = relativeLeft + 'px';
        win.style.top = relativeTop + 'px';
        
        bringToFront(win.dataset.windowId);
        e.preventDefault();
    }

    function handleDrag(e) {
        if (!dragState) return;
        
        const dx = e.clientX - dragState.startX;
        const dy = e.clientY - dragState.startY;
        
        let newLeft = dragState.startLeft + dx;
        let newTop = dragState.startTop + dy;
        
        // Get current container rect (may have changed due to resize)
        const container = dragState.container;
        const winRect = dragState.win.getBoundingClientRect();
        
        if (container) {
            const containerRect = container.getBoundingClientRect();
            const containerWidth = containerRect.width;
            const containerHeight = containerRect.height;
            const winWidth = winRect.width;
            const winHeight = winRect.height;
            
            // Keep window within container bounds (allow 50px overflow for partial visibility)
            const minVisible = 50;
            newLeft = Math.max(-winWidth + minVisible, Math.min(newLeft, containerWidth - minVisible));
            newTop = Math.max(0, Math.min(newTop, containerHeight - minVisible));
        }
        
        dragState.win.style.left = newLeft + 'px';
        dragState.win.style.top = newTop + 'px';
    }

    function startResize(e, win) {
        const rect = win.getBoundingClientRect();
        resizeState = {
            win: win,
            startX: e.clientX,
            startY: e.clientY,
            startWidth: rect.width,
            startHeight: rect.height
        };
        bringToFront(win.dataset.windowId);
        e.preventDefault();
    }

    function handleResize(e) {
        if (!resizeState) return;
        
        const dx = e.clientX - resizeState.startX;
        const dy = e.clientY - resizeState.startY;
        
        const newWidth = Math.max(250, resizeState.startWidth + dx);
        const newHeight = Math.max(200, resizeState.startHeight + dy);
        
        resizeState.win.style.width = newWidth + 'px';
        resizeState.win.style.height = newHeight + 'px';
    }

    // Monotonically increasing z-index counter for proper card stacking.
    // All windows share values in the range [WIN_Z_BASE, WIN_Z_BASE + n].
    // Toolbars live at z-index 600 (toolbar.css) which is above #floating-windows (z-index 500),
    // but the individual window elements are children of #floating-windows.
    // Their z-index is relative to that stacking context, so any positive value keeps them
    // inside the container — the container itself (z-index 500) is the "ceiling" vs the toolbar (600).
    // We use a counter so clicking a window always raises it above all siblings.
    const WIN_Z_BASE = 10;
    let _winZCounter = WIN_Z_BASE;

    function bringToFront(windowId) {
        // Assign the next counter value — always strictly greater than any previous
        _winZCounter += 1;
        if (windows[windowId]) {
            windows[windowId].style.zIndex = String(_winZCounter);
            activeWindow = windowId;
        }
        // Raise our container above the task-windows container so clicking a regular
        // card always beats any task card, and vice-versa when a task card is clicked.
        const myContainer = document.getElementById('floating-windows');
        const taskContainer = document.getElementById('task-floating-windows');
        if (myContainer) myContainer.style.zIndex = '501';
        if (taskContainer) taskContainer.style.zIndex = '500';
    }

    // ============================================
    // Window API
    // ============================================
    function open(windowId) {
        let win = windows[windowId];
        
        if (!win) {
            win = createWindow(windowId);
        }
        
        if (win) {
            win.classList.add('visible');
            bringToFront(windowId);
            updateToolbarButton(windowId, true);
            
            // Update legend when opening
            if (windowId === 'legend') {
                updateLegend();
            }
            
            // Update aliases when opening aliases window
            if (windowId === 'aliases') {
                if (typeof ExplorerAPI !== 'undefined' && ExplorerAPI.getAliases) {
                    const aliases = ExplorerAPI.getAliases();
                    if (aliases && aliases.length > 0) {
                        ExplorerAPI.updateAliasesList(aliases);
                    }
                }
                // Initialize Lucide icons
                if (typeof lucide !== 'undefined') {
                    setTimeout(() => lucide.createIcons(), 10);
                }
            }
            
            // Update selection/operators when opening selection window
            if (windowId === 'selection') {
                updateOperatorStates();
                // Show current selection if any
                if (selectedNodesData.length > 0) {
                    showNodeDetails(selectedNodesData);
                }
                // Initialize Lucide icons
                if (typeof lucide !== 'undefined') {
                    setTimeout(() => lucide.createIcons(), 10);
                }
            }
            
            // Update operators when opening operators window (legacy)
            if (windowId === 'operators') {
                updateOperatorStates();
                // Initialize Lucide icons
                if (typeof lucide !== 'undefined') {
                    setTimeout(() => lucide.createIcons(), 10);
                }
            }
            
            // Explorer window initialization
            if (windowId === 'explorer') {
                // Initialize Lucide icons
                if (typeof lucide !== 'undefined') {
                    setTimeout(() => lucide.createIcons(), 10);
                }
            }
            
            // Load FQL history when opening FQL window
            if (windowId === 'fql') {
                loadFqlHistory();
                // Hide result section initially
                hideFqlResult();
            }
        }
    }

    function close(windowId) {
        const win = windows[windowId];
        if (win) {
            win.classList.remove('visible');
            updateToolbarButton(windowId, false);
        }
    }

    function toggle(windowId) {
        const win = windows[windowId];
        if (win && win.classList.contains('visible')) {
            close(windowId);
        } else {
            open(windowId);
        }
    }

    function isOpen(windowId) {
        const win = windows[windowId];
        return win && win.classList.contains('visible');
    }

    function updateToolbarButton(windowId, active) {
        const btnId = {
            'explorer': 'btn-explorer',
            'selection': 'btn-selection',
            'find': 'btn-explorer',      // Legacy
            'search': 'btn-explorer',    // Legacy
            'details': 'btn-selection',  // Legacy
            'operators': 'btn-selection', // Legacy
            'aliases': 'btn-aliases',
            'legend': 'btn-legend'
        }[windowId];
        
        const btn = document.getElementById(btnId);
        if (btn) {
            if (active) {
                btn.classList.add('active');
                btn.setAttribute('aria-pressed', 'true');
            } else {
                btn.classList.remove('active');
                btn.setAttribute('aria-pressed', 'false');
            }
        }
    }

    // ============================================
    // Find Window Logic
    // ============================================
    let selectedFindNodes = [];
    
    function handleFindInput(e) {
        const query = e.target.value.toLowerCase().trim();
        const resultsContainer = document.getElementById('find-results');
        const filterType = document.getElementById('find-filter-type')?.value || 'all';
        const filterValue = document.getElementById('find-filter-value')?.value.toLowerCase().trim() || '';
        
        if (!resultsContainer) return;
        
        const nodes = GraphExplorer.getState().nodes;
        if (!nodes) return;
        
        let matches = [];
        nodes.forEach(function(node) {
            const label = (node.label || node.id || '').toLowerCase();
            
            // Apply text search filter
            const matchesQuery = !query || label.includes(query);
            if (!matchesQuery) return;
            
            // Apply attribute filter
            if (filterValue) {
                const attrValue = (node[filterType] || '').toString().toLowerCase();
                if (!attrValue.includes(filterValue)) return;
            }
            
            matches.push(node);
        });
        
        if (matches.length === 0 && (query || filterValue)) {
            safeSetInnerHTML(resultsContainer, '<div class="window-empty">No nodes found</div>');
            return;
        }
        
        // Sort by label
        matches.sort((a, b) => (a.label || a.id).localeCompare(b.label || b.id));
        
        // Show results (limit to 100)
        const displayMatches = matches.slice(0, 100);
        safeSetInnerHTML(resultsContainer, displayMatches.map(function(node) {
            const isSelected = selectedFindNodes.includes(node.id);
            return `
                <div class="find-result-item ${isSelected ? 'selected' : ''}" data-node-id="${node.id}">
                    <span class="find-result-name">${node.label || node.id}</span>
                    <span class="find-result-type">${node.resource_name || node.type || ''}</span>
                </div>
            `;
        }).join(''));
        
        if (matches.length > 100) {
            safeInsertAdjacentHTML(resultsContainer, 'beforeend', `<div class="find-more">+${matches.length - 100} more (refine search)</div>`);
        }
        
        // Bind click events with CTRL multi-select support
        resultsContainer.querySelectorAll('.find-result-item').forEach(function(item) {
            item.addEventListener('click', function(e) {
                const nodeId = this.dataset.nodeId;
                
                if (e.ctrlKey || e.metaKey) {
                    // Toggle selection
                    const idx = selectedFindNodes.indexOf(nodeId);
                    if (idx >= 0) {
                        selectedFindNodes.splice(idx, 1);
                        this.classList.remove('selected');
                    } else {
                        selectedFindNodes.push(nodeId);
                        this.classList.add('selected');
                    }
                    
                    // Update network selection
                    if (selectedFindNodes.length > 0) {
                        GraphExplorer.getState().network.selectNodes(selectedFindNodes);
                        GraphExplorer.getState().selectedNodes = selectedFindNodes;
                        GraphExplorer.getState().selectedAlias = null;
                        if (typeof GraphExplorer.highlightMultipleNodes === 'function') {
                            GraphExplorer.highlightMultipleNodes(selectedFindNodes);
                        }
                        // Update selection window
                        const nodeData = selectedFindNodes.map(id => GraphExplorer.getState().nodes.get(id));
                        showNodeDetails(nodeData);
                    }
                } else {
                    // Single select
                    selectedFindNodes = [nodeId];
                    resultsContainer.querySelectorAll('.find-result-item').forEach(el => el.classList.remove('selected'));
                    this.classList.add('selected');
                    
                    GraphExplorer.focusNode(nodeId);
                    GraphExplorer.selectNode(nodeId);
                    
                    // Update selection window
                    const nodeData = GraphExplorer.getState().nodes.get(nodeId);
                    showNodeDetails([nodeData]);
                }
            });
        });
    }
    
    function handleFindFilterChange() {
        // Trigger re-search when filter changes
        const searchInput = document.getElementById('find-search');
        if (searchInput) {
            handleFindInput({ target: searchInput });
        }
    }

    function updateFscSelectionPanel(win) {
        const state = GraphExplorer.getState ? GraphExplorer.getState() : {};
        const selectedIds = state.selectedNodes || [];
        const emptyEl = win.querySelector('#fsc-details-empty');
        const contentEl = win.querySelector('#fsc-details-content');
        const summaryEl = win.querySelector('#fsc-selection-summary');
        const listEl = win.querySelector('#fsc-selected-nodes-list');
        if (!emptyEl || !contentEl) return;
        if (selectedIds.length === 0) {
            emptyEl.style.display = '';
            contentEl.style.display = 'none';
            return;
        }
        emptyEl.style.display = 'none';
        contentEl.style.display = '';
        if (summaryEl) summaryEl.querySelector('.selection-count').textContent = `${selectedIds.length} node${selectedIds.length > 1 ? 's' : ''} selected`;
        if (listEl && state.nodes) {
            safeSetInnerHTML(listEl, selectedIds.map(id => {
                const node = state.nodes.get(id);
                if (!node) return '';
                return `<div class="selected-node-item">
                    <span class="node-label">${node.label || id}</span>
                    <span class="node-type-badge">${node.resource_name || node.type || ''}</span>
                </div>`;
            }).join(''));
        }
    }

    // ============================================
    // Details Window Logic (Multi-select support)
    // ============================================
    function showNodeDetails(nodeDataArray) {
        // Accept single node or array
        if (!Array.isArray(nodeDataArray)) {
            nodeDataArray = nodeDataArray ? [nodeDataArray] : [];
        }
        
        selectedNodesData = nodeDataArray;
        
        // Sync selection with GraphExplorer state for operator execution
        if (typeof GraphExplorer !== 'undefined' && GraphExplorer.state) {
            GraphExplorer.state.selectedNodes = nodeDataArray.map(node => node.id).filter(Boolean);
            GraphExplorer.state.selectedAlias = null;
        }
        
        const emptyEl = document.getElementById('details-empty');
        const contentEl = document.getElementById('details-content');
        const summaryEl = document.getElementById('selection-summary');
        const listEl = document.getElementById('selected-nodes-list');
        
        if (nodeDataArray.length === 0) {
            if (emptyEl) emptyEl.style.display = 'flex';
            if (contentEl) contentEl.style.display = 'none';
            updateWindowTitle('selection', 'Selection & Operators');
            updateWindowTitle('details', 'Selection');  // Legacy support
            updateOperatorStates(0);
            return;
        }
        
        if (emptyEl) emptyEl.style.display = 'none';
        if (contentEl) contentEl.style.display = 'block';
        
        // Update summary
        if (summaryEl) {
            const count = nodeDataArray.length;
            safeSetInnerHTML(summaryEl, `<span class="selection-count">${count} node${count > 1 ? 's' : ''} selected</span>`);
        }
        
        // Render node items (collapsible)
        if (listEl) {
            safeSetInnerHTML(listEl, nodeDataArray.map((nodeData, idx) => {
                const network = GraphExplorer.getState().network;
                const connections = network ? network.getConnectedNodes(nodeData.id).length : 0;
                const expanded = nodeDataArray.length === 1 ? 'expanded' : '';
                
                // Get properties
                const skipProps = ['id', 'label', 'color', 'size', 'x', 'y', 'hidden', 'physics', 'resource_name', 'type', 'domain', 'font', 'borderWidth', 'borderWidthSelected', 'opacity', 'shape', 'title', 'mixin_name', 'node_type', 'member_count', 'sample_ids', 'sample_labels'];
                const props = Object.entries(nodeData)
                    .filter(([key]) => !skipProps.includes(key) && !key.startsWith('_'))
                    .slice(0, 8);

                return `
                    <div class="node-item ${expanded}" data-node-id="${nodeData.id}">
                        <div class="node-item-header">
                            <span class="node-item-expand">▶</span>
                            <span class="node-item-color" style="background: ${getNodeColor(nodeData)}"></span>
                            <span class="node-item-name">${nodeData.label || nodeData.id}</span>
                            <span class="node-item-type">${nodeData.resource_name || nodeData.type || ''}</span>
                        </div>
                        <div class="node-item-details">
                            <div class="node-detail-row">
                                <span class="node-detail-label">ID</span>
                                <span class="node-detail-value">${nodeData.id}</span>
                            </div>
                            <div class="node-detail-row">
                                <span class="node-detail-label">Domain</span>
                                <span class="node-detail-value">${nodeData.domain || '-'}</span>
                            </div>
                            <div class="node-detail-row">
                                <span class="node-detail-label">Connections</span>
                                <span class="node-detail-value">${connections}</span>
                            </div>
                            ${props.length > 0 ? `
                                <div class="node-detail-props">
                                    ${props.map(([key, val]) => `
                                        <div class="node-detail-prop">
                                            <span class="prop-key">${key}:</span>
                                            <span class="prop-value">${val}</span>
                                        </div>
                                    `).join('')}
                                </div>
                            ` : ''}
                        </div>
                    </div>
                `;
            }).join(''));
        }
        
        // Update window title (support both old 'details' and new 'selection' windows)
        const count = nodeDataArray.length;
        const title = count === 1 ? (nodeDataArray[0].label || 'Node Details') : `${count} Nodes Selected`;
        updateWindowTitle('selection', title);
        updateWindowTitle('details', title);  // Legacy support
        
        // Update operator states based on selection
        updateOperatorStates(count);

        // Sync to findSelect window panel if open
        const fscWin = windows['findSelect'];
        if (fscWin) updateFscSelectionPanel(fscWin);
    }

    function addNodeToSelection(nodeData) {
        // Check if already in selection
        const exists = selectedNodesData.find(n => n.id === nodeData.id);
        if (!exists) {
            selectedNodesData.push(nodeData);
            showNodeDetails(selectedNodesData);
        }
    }

    function removeNodeFromSelection(nodeId) {
        selectedNodesData = selectedNodesData.filter(n => n.id !== nodeId);
        showNodeDetails(selectedNodesData);
    }

    function clearDetails() {
        selectedNodesData = [];
        showNodeDetails([]);
    }

    function getSelectedNodes() {
        return selectedNodesData;
    }

    function getNodeColor(nodeData) {
        if (typeof nodeData.color === 'string') return nodeData.color;
        if (nodeData.color && nodeData.color.background) return nodeData.color.background;
        return '#666';
    }

    function updateWindowTitle(windowId, title) {
        const win = windows[windowId];
        if (win) {
            const titleEl = win.querySelector('.floating-window-title');
            if (titleEl) {
                titleEl.textContent = title;
            }
        }
    }

    // ============================================
    // Legend Window Logic
    // ============================================

    // Track current legend filter state for visual feedback
    let legendActiveNodeType = null;
    let legendActiveEdgeName = null;

    function updateLegend() {
        const state = GraphExplorer.getState();
        const colorAttrEl = document.getElementById('legend-color-attr');
        const colorItemsEl = document.getElementById('legend-color-items');
        const edgeItemsEl = document.getElementById('legend-edge-items');
        const sizeAttrEl = document.getElementById('legend-size-attr');

        // --- Sync active filter state from API ---
        if (typeof ExplorerAPI !== 'undefined' && ExplorerAPI.getActiveFilters) {
            const filters = ExplorerAPI.getActiveFilters();
            legendActiveNodeType = filters.nodeType || null;
            legendActiveEdgeName = filters.edgeName || null;
        }

        // --- Color / Node legend ---
        if (colorAttrEl) {
            colorAttrEl.textContent = state.currentColorAttribute || 'domain';
        }

        if (colorItemsEl) {
            const legendMap = state.legendColorMap || {};
            const entries = Object.entries(legendMap);

            if (entries.length > 0) {
                entries.sort((a, b) => a[0].localeCompare(b[0]));

                safeSetInnerHTML(colorItemsEl, entries.slice(0, 20).map(([label, color]) => {
                    const isActive = legendActiveNodeType && legendActiveNodeType.split(',').map(s => s.trim()).includes(label);
                    return `
                    <div class="legend-item clickable${isActive ? ' legend-filter-active' : ''}" 
                         data-legend-type="node"
                         data-legend-value="${label}"
                         title="Click to highlight all '${label}' nodes\nDouble-click to filter graph to this type">
                        <span class="legend-color" style="background: ${color}"></span>
                        <span class="legend-label">${label}</span>
                        ${isActive ? '<span class="legend-filter-badge">filtered</span>' : ''}
                    </div>`;
                }).join(''));

                if (entries.length > 20) {
                    safeInsertAdjacentHTML(colorItemsEl, 'beforeend', `<div class="legend-more">+${entries.length - 20} more...</div>`);
                }

                _bindLegendItemHandlers(colorItemsEl);
            } else {
                safeSetInnerHTML(colorItemsEl, '<div class="window-empty small">No color mapping</div>');
            }
        }

        // --- Edge legend ---
        if (edgeItemsEl) {
            const edgeLabelColors = {};
            if (state.edges) {
                state.edges.forEach(edge => {
                    const label = edge.label || edge.name || 'unnamed';
                    if (!edgeLabelColors[label]) {
                        // Extract color from edge object
                        let color = '#7f8c8d';
                        if (typeof edge.color === 'string') {
                            color = edge.color;
                        } else if (edge.color && edge.color.color) {
                            color = edge.color.color;
                        }
                        edgeLabelColors[label] = color;
                    }
                });
            }

            const edgeEntries = Object.entries(edgeLabelColors).sort((a, b) => a[0].localeCompare(b[0]));

            if (edgeEntries.length > 0) {
                safeSetInnerHTML(edgeItemsEl, edgeEntries.slice(0, 20).map(([label, color]) => {
                    const isActive = legendActiveEdgeName && legendActiveEdgeName.split(',').map(s => s.trim()).includes(label);
                    return `
                    <div class="legend-item clickable${isActive ? ' legend-filter-active' : ''}" 
                         data-legend-type="edge"
                         data-legend-value="${label}"
                         title="Click to highlight all '${label}' edges\nDouble-click to filter to include this edge type">
                        <span class="legend-edge-line" style="background: ${color}"></span>
                        <span class="legend-label">${label}</span>
                        ${isActive ? '<span class="legend-filter-badge">filtered</span>' : ''}
                    </div>`;
                }).join(''));

                if (edgeEntries.length > 20) {
                    safeInsertAdjacentHTML(edgeItemsEl, 'beforeend', `<div class="legend-more">+${edgeEntries.length - 20} more...</div>`);
                }

                _bindLegendItemHandlers(edgeItemsEl);
            } else {
                safeSetInnerHTML(edgeItemsEl, '<div class="window-empty small">No edges in graph</div>');
            }
        }

        // --- Size legend ---
        if (sizeAttrEl) {
            sizeAttrEl.textContent = 'Scale: ' + (state.currentSizeScale || 2);
        }

        // --- Aggregation legend (removed — diamond aggregation replaced by vis.js clustering) ---
        let aggSectionEl = document.getElementById('legend-aggregation-items');
        if (aggSectionEl) {
            aggSectionEl.parentElement?.remove();
        }

        // --- Filter bar ---
        _updateLegendFilterBar();

        // --- Clear filter button ---
        const clearBtn = document.getElementById('legend-filter-clear');
        if (clearBtn && !clearBtn._bound) {
            clearBtn._bound = true;
            clearBtn.addEventListener('click', () => {
                clearLegendFilters();
            });
        }
    }

    /**
     * Bind single-click (highlight) and double-click (filter) handlers to legend items.
     * @private
     */
    function _bindLegendItemHandlers(container) {
        container.querySelectorAll('.legend-item.clickable').forEach(item => {
            // Prevent double-firing of click on dblclick
            let clickTimer = null;

            item.addEventListener('click', (e) => {
                // Wait briefly to see if a dblclick follows
                clearTimeout(clickTimer);
                clickTimer = setTimeout(() => {
                    const type = item.getAttribute('data-legend-type');
                    const value = item.getAttribute('data-legend-value');
                    if (type === 'node') {
                        selectNodesByResourceName(value);
                    } else if (type === 'edge') {
                        highlightEdgesByName(value);
                    }
                }, 220);
            });

            item.addEventListener('dblclick', (e) => {
                clearTimeout(clickTimer);
                const type = item.getAttribute('data-legend-type');
                const value = item.getAttribute('data-legend-value');
                if (type === 'node') {
                    filterGraphToNodeType(value);
                } else if (type === 'edge') {
                    filterGraphToEdgeName(value);
                }
            });
        });
    }

    /**
     * Update the filter bar visibility and description text.
     * @private
     */
    function _updateLegendFilterBar() {
        const bar = document.getElementById('legend-filter-bar');
        const desc = document.getElementById('legend-filter-desc');
        if (!bar) return;

        const hasFilter = legendActiveNodeType || legendActiveEdgeName;
        bar.style.display = hasFilter ? 'flex' : 'none';

        if (desc) {
            const parts = [];
            if (legendActiveNodeType) parts.push(`Nodes: ${legendActiveNodeType}`);
            if (legendActiveEdgeName) parts.push(`Edges: ${legendActiveEdgeName}`);
            desc.textContent = parts.join(' · ');
        }
    }

    /**
     * Single-click: highlight all nodes matching this color-attribute value.
     */
    function selectNodesByResourceName(resourceName) {
        const state = GraphExplorer.getState();
        if (!state.nodes) {
            console.warn('[ExplorerWindows] No nodes available for selection');
            return;
        }

        const colorAttr = state.currentColorAttribute || 'resource_name';

        const matchingNodeIds = [];
        state.nodes.forEach((node) => {
            if (node[colorAttr] === resourceName) {
                matchingNodeIds.push(node.id);
            }
        });

        if (matchingNodeIds.length === 0) {
            ExplorerUtils.announceChange(`No '${resourceName}' nodes found`);
            return;
        }

        if (state.network) {
            state.network.selectNodes(matchingNodeIds);
            state.selectedNodes = matchingNodeIds;
            state.selectedAlias = null;

            if (typeof GraphExplorer.highlightMultipleNodes === 'function') {
                GraphExplorer.highlightMultipleNodes(matchingNodeIds);
            }

            if (typeof GraphExplorer !== 'undefined' && GraphExplorer.syncNetworkSize) GraphExplorer.syncNetworkSize();
            state.network.fit({ nodes: matchingNodeIds, animation: false });
            ExplorerUtils.announceChange(`Selected ${matchingNodeIds.length} '${resourceName}' nodes`);

            if (typeof ExplorerAPI !== 'undefined' && ExplorerAPI.updateSelectionWindow) {
                ExplorerAPI.updateSelectionWindow(matchingNodeIds, null);
            } else {
                showNodeDetails(matchingNodeIds.map(id => state.nodes.get(id)));
            }

            updateOperatorStates(matchingNodeIds.length);
        }
    }

    /**
     * Single-click: highlight all edges with this label.
     */
    function highlightEdgesByName(edgeName) {
        const state = GraphExplorer.getState();
        if (!state.edges || !state.network) return;

        const matchingEdgeIds = [];
        const connectedNodeIdSet = new Set();
        state.edges.forEach(edge => {
            if ((edge.label || edge.name || 'unnamed') === edgeName) {
                matchingEdgeIds.push(edge.id);
                if (edge.from != null) connectedNodeIdSet.add(edge.from);
                if (edge.to != null) connectedNodeIdSet.add(edge.to);
            }
        });

        if (matchingEdgeIds.length === 0) {
            ExplorerUtils.announceChange(`No '${edgeName}' edges found`);
            return;
        }

        const connectedNodeIds = Array.from(connectedNodeIdSet);

        // Dim all nodes/edges not part of this edge group
        state.nodes.forEach(node => {
            state.nodes.update({
                id: node.id,
                opacity: connectedNodeIdSet.has(node.id) ? 1 : 0.15
            });
        });
        state.edges.forEach(edge => {
            state.edges.update({
                id: edge.id,
                opacity: matchingEdgeIds.includes(edge.id) ? 1 : 0.1
            });
        });

        // Select edges and connected nodes
        state.network.selectEdges(matchingEdgeIds);
        state.network.selectNodes(connectedNodeIds);
        state.selectedNodes = connectedNodeIds;
        state.selectedAlias = null;

        // Fit to the affected nodes
        if (connectedNodeIds.length > 0) {
            if (typeof GraphExplorer !== 'undefined' && GraphExplorer.syncNetworkSize) GraphExplorer.syncNetworkSize();
            state.network.fit({ nodes: connectedNodeIds, animation: false });
        }

        // Update selection panel
        if (typeof ExplorerAPI !== 'undefined' && ExplorerAPI.updateSelectionWindow) {
            ExplorerAPI.updateSelectionWindow(connectedNodeIds, null);
        } else {
            showNodeDetails(connectedNodeIds.map(id => state.nodes.get(id)));
        }

        // Enable operator buttons
        updateOperatorStates(connectedNodeIds.length);

        ExplorerUtils.announceChange(`Selected ${matchingEdgeIds.length} '${edgeName}' edges (${connectedNodeIds.length} connected nodes)`);
    }

    /**
     * Double-click on node legend item: filter the graph to only show this node type.
     * Calls /graph/data with include_node_type param and refreshes.
     */
    function filterGraphToNodeType(nodeType) {
        if (typeof ExplorerAPI === 'undefined' || !ExplorerAPI.setNodeTypeFilter) return;

        // Toggle: if this type is already the active filter, clear it
        const existing = ExplorerAPI.getActiveFilters().nodeType;
        const existingTypes = existing ? existing.split(',').map(s => s.trim()) : [];
        const idx = existingTypes.indexOf(nodeType);

        if (idx >= 0) {
            // Already active — remove it
            existingTypes.splice(idx, 1);
            ExplorerAPI.setNodeTypeFilter(existingTypes.length > 0 ? existingTypes.join(',') : null);
        } else {
            // Add to filter
            existingTypes.push(nodeType);
            ExplorerAPI.setNodeTypeFilter(existingTypes.join(','));
        }

        legendActiveNodeType = ExplorerAPI.getActiveFilters().nodeType;
        ExplorerUtils.announceChange(legendActiveNodeType ? `Filtering to node types: ${legendActiveNodeType}` : 'Node type filter cleared');

        // Refresh graph with new filter
        ExplorerAPI.refreshGraph(true).then(() => updateLegend());
    }

    /**
     * Double-click on edge legend item: add this edge name to the extra include filter.
     * Calls /graph/data with include_edge_name param and refreshes.
     */
    function filterGraphToEdgeName(edgeName) {
        if (typeof ExplorerAPI === 'undefined' || !ExplorerAPI.setEdgeNameFilter) return;

        // Toggle: if this edge name is already active, remove it
        const existing = ExplorerAPI.getActiveFilters().edgeName;
        const existingNames = existing ? existing.split(',').map(s => s.trim()) : [];
        const idx = existingNames.indexOf(edgeName);

        if (idx >= 0) {
            existingNames.splice(idx, 1);
            ExplorerAPI.setEdgeNameFilter(existingNames.length > 0 ? existingNames.join(',') : null);
        } else {
            existingNames.push(edgeName);
            ExplorerAPI.setEdgeNameFilter(existingNames.join(','));
        }

        legendActiveEdgeName = ExplorerAPI.getActiveFilters().edgeName;
        ExplorerUtils.announceChange(legendActiveEdgeName ? `Edge filter: ${legendActiveEdgeName}` : 'Edge filter cleared');

        // Refresh graph with new filter
        ExplorerAPI.refreshGraph(true).then(() => updateLegend());
    }

    /**
     * Clear all active legend filters and refresh graph.
     */
    function clearLegendFilters() {
        if (typeof ExplorerAPI !== 'undefined' && ExplorerAPI.clearAllFilters) {
            ExplorerAPI.clearAllFilters();
        }
        legendActiveNodeType = null;
        legendActiveEdgeName = null;
        ExplorerUtils.announceChange('All legend filters cleared');
        ExplorerAPI.refreshGraph(true).then(() => updateLegend());
    }

    // ============================================
    // Algorithm Execution
    // ============================================
    function handleAlgorithmClick(algoId) {
        console.log('[ExplorerWindows] Algorithm clicked:', algoId);
        
        // Parse category.algorithm format
        const parts = algoId.split('.');
        const category = parts[0];
        const algorithm = parts[1];
        
        // Show algorithm modal/configuration
        if (typeof ExplorerModal !== 'undefined') {
            ExplorerModal.open('algorithm', {
                category: category,
                algorithm: algorithm,
                algoId: algoId
            });
        } else {
            // Fallback: announce the action
            if (typeof GraphExplorer !== 'undefined') {
                ExplorerUtils.announceChange(`Running ${algorithm} algorithm...`);
            }
            console.log(`[ExplorerWindows] Would execute: ALGO ${category.toUpperCase()}.${algorithm.toUpperCase()}()`);
        }
    }

    // ============================================
    // Function Execution
    // ============================================
    function handleFunctionClick(functionId) {
        console.log('[ExplorerWindows] Function clicked:', functionId);
        
        // Find the function definition
        let functionDef = null;
        for (const cat of functionCategories) {
            const fn = cat.functions.find(f => f.id === functionId);
            if (fn) {
                functionDef = fn;
                break;
            }
        }
        
        if (!functionDef) {
            console.warn('[ExplorerWindows] Function not found:', functionId);
            return;
        }
        
        // Show function modal/configuration
        if (typeof ExplorerModal !== 'undefined') {
            ExplorerModal.open('function', {
                functionId: functionId,
                name: functionDef.name,
                description: functionDef.description,
                algo: functionDef.algo
            });
        } else {
            // Fallback: announce the action
            if (typeof GraphExplorer !== 'undefined') {
                ExplorerUtils.announceChange(`Running function: ${functionDef.name}...`);
            }
            console.log(`[ExplorerWindows] Would execute function: ${functionDef.name} using ${functionDef.algo}`);
        }
    }

    // ============================================
    // Layout & Physics Settings Handlers
    // ============================================
    function handleLayoutChange(layout, win) {
        const isHierarchical = layout.startsWith('hierarchical');
        
        // Show/hide hierarchical options
        const hierarchicalOptions = win.querySelectorAll('.settings-hierarchical-options');
        hierarchicalOptions.forEach(el => {
            el.style.display = isHierarchical ? 'flex' : 'none';
        });

        if (typeof GraphExplorer !== 'undefined' && GraphExplorer.setLayout) {
            GraphExplorer.setLayout(layout);
        }
        console.log('[ExplorerWindows] Layout changed to:', layout);
    }

    function handleHierarchicalSortChange(sortMethod) {
        if (typeof GraphExplorer !== 'undefined' && GraphExplorer.setHierarchicalSort) {
            GraphExplorer.setHierarchicalSort(sortMethod);
        }
    }

    function handleLevelSeparationChange(value) {
        if (typeof GraphExplorer !== 'undefined' && GraphExplorer.setLevelSeparation) {
            GraphExplorer.setLevelSeparation(value);
        }
    }

    function handlePhysicsToggle(enabled, win) {
        const paramsDiv = win.querySelector('#settings-physics-params');
        if (paramsDiv) {
            paramsDiv.style.opacity = enabled ? '1' : '0.5';
            paramsDiv.style.pointerEvents = enabled ? 'auto' : 'none';
        }
        if (typeof GraphExplorer !== 'undefined') {
            if (enabled) {
                GraphExplorer.enablePhysics();
            } else {
                GraphExplorer.disablePhysics();
            }
        }
    }

    function handleSolverChange(solver, win) {
        if (typeof GraphExplorer !== 'undefined' && GraphExplorer.setPhysicsSolver) {
            GraphExplorer.setPhysicsSolver(solver);
        }
        // Update slider values to match new solver defaults
        const defaults = (typeof ExplorerConstants !== 'undefined' && ExplorerConstants.DEFAULT_PHYSICS_OPTIONS)
            ? ExplorerConstants.DEFAULT_PHYSICS_OPTIONS[solver] || {}
            : {};
        updatePhysicsSliders(win, defaults);
    }

    function handlePhysicsParamChange(param, value) {
        if (typeof GraphExplorer !== 'undefined' && GraphExplorer.setPhysicsParam) {
            GraphExplorer.setPhysicsParam(param, value);
        }
    }

    function handlePhysicsResetDefaults(win) {
        const solverSelect = win.querySelector('#settings-physics-solver');
        const solver = solverSelect ? solverSelect.value : 'barnesHut';
        const defaults = (typeof ExplorerConstants !== 'undefined' && ExplorerConstants.DEFAULT_PHYSICS_OPTIONS)
            ? ExplorerConstants.DEFAULT_PHYSICS_OPTIONS[solver] || {}
            : {};
        
        updatePhysicsSliders(win, defaults);
        
        if (typeof GraphExplorer !== 'undefined' && GraphExplorer.resetPhysicsDefaults) {
            GraphExplorer.resetPhysicsDefaults(solver);
        }
    }

    function handlePhysicsStabilize() {
        if (typeof GraphExplorer !== 'undefined' && GraphExplorer.improveLayout) {
            GraphExplorer.improveLayout(200);
        }
    }

    /**
     * Update all physics slider positions and displayed values to match given defaults.
     */
    function updatePhysicsSliders(win, defaults) {
        const sliderMap = [
            { id: 'settings-spring-length', valueId: 'spring-length-value', key: 'springLength', fallback: 200, isInt: true },
            { id: 'settings-spring-constant', valueId: 'spring-constant-value', key: 'springConstant', fallback: 0.04, isInt: false },
            { id: 'settings-repulsion-strength', valueId: 'repulsion-strength-value', key: 'gravitationalConstant', fallback: -2000, isInt: true },
            { id: 'settings-central-gravity', valueId: 'central-gravity-value', key: 'centralGravity', fallback: 0.3, isInt: false },
            { id: 'settings-damping', valueId: 'damping-value', key: 'damping', fallback: 0.2, isInt: false }
        ];

        sliderMap.forEach(({ id, valueId, key, fallback, isInt }) => {
            const slider = win.querySelector('#' + id);
            const valueEl = win.querySelector('#' + valueId);
            const val = defaults[key] !== undefined ? defaults[key] : fallback;
            if (slider) slider.value = val;
            if (valueEl) valueEl.textContent = isInt ? val : val;
        });
    }

    // ============================================
    // Settings Handlers
    // ============================================
    function handleSettingsNodeLabelsToggle(visible) {
        console.log('[ExplorerWindows] Node labels visibility:', visible);
        if (typeof GraphExplorer !== 'undefined') {
            GraphExplorer.toggleNodeLabels(visible);
        }
    }

    function handleSettingsEdgeLabelsToggle(visible) {
        console.log('[ExplorerWindows] Edge labels visibility:', visible);
        if (typeof GraphExplorer !== 'undefined') {
            GraphExplorer.toggleEdgeLabels(visible);
        }
    }

    function handleSettingsColorChange(attribute) {
        console.log('[ExplorerWindows] Color changed to:', attribute);
        if (typeof GraphExplorer !== 'undefined') {
            GraphExplorer.setColorByAttribute(attribute);
            updateLegend();
        }
    }

    function handleSettingsSizeByChange(attribute) {
        console.log('[ExplorerWindows] Size by changed to:', attribute);
        const state = GraphExplorer?.getState();
        if (!state?.nodes || !state?.network) return;

        const nodes = state.nodes.get();
        const network = state.network;
        
        if (attribute === 'fixed') {
            // Reset to fixed size
            const updates = nodes.map(node => ({
                id: node.id,
                size: 25
            }));
            state.nodes.update(updates);
        } else if (attribute === 'degree') {
            // Size by number of connections
            nodes.forEach(node => {
                const connections = network.getConnectedNodes(node.id).length;
                const size = Math.max(10, Math.min(50, 15 + connections * 3));
                state.nodes.update({ id: node.id, size: size });
            });
        } else if (attribute === 'betweenness') {
            // Placeholder: would need actual betweenness calculation
            console.log('[ExplorerWindows] Betweenness centrality sizing not yet implemented');
        } else if (attribute === 'closeness') {
            console.log('[ExplorerWindows] Closeness centrality sizing not yet implemented');
        } else if (attribute === 'pagerank') {
            console.log('[ExplorerWindows] PageRank sizing not yet implemented');
        }
        
        state.currentSizeBy = attribute;
    }

    function handleSettingsSizeChange(scale, win) {
        console.log('[ExplorerWindows] Size scale changed to:', scale);
        if (typeof GraphExplorer !== 'undefined') {
            GraphExplorer.setSizeScale(scale);
        }
        // Update active button state
        win.querySelectorAll('.settings-scale-btn').forEach(btn => {
            btn.classList.toggle('active', parseInt(btn.dataset.scale) === scale);
        });
    }

    function handleNodeFontChange(size) {
        console.log('[ExplorerWindows] Node font size changed to:', size);
        const state = GraphExplorer?.getState();
        if (state?.nodes) {
            const updates = state.nodes.get().map(node => ({
                id: node.id,
                font: { ...node.font, size: size }
            }));
            state.nodes.update(updates);
        }
    }

    function handleEdgeFontChange(size) {
        console.log('[ExplorerWindows] Edge font size changed to:', size);
        const state = GraphExplorer?.getState();
        if (state?.edges) {
            const updates = state.edges.get().map(edge => ({
                id: edge.id,
                font: { ...edge.font, size: size }
            }));
            state.edges.update(updates);
        }
    }

    function handleEdgeColorChange(attribute) {
        console.log('[ExplorerWindows] Edge color by changed to:', attribute);
        const state = GraphExplorer?.getState();
        if (!state?.edges) return;

        const edges = state.edges.get();
        
        if (attribute === 'default') {
            // Reset to default color
            const updates = edges.map(edge => ({
                id: edge.id,
                color: { inherit: 'both' }
            }));
            state.edges.update(updates);
        } else if (attribute === 'name' || attribute === 'type') {
            // Color by edge name/type
            const edgeColors = {};
            const palette = ['#4299E1', '#48BB78', '#ED8936', '#9F7AEA', '#F56565', '#38B2AC', '#ECC94B'];
            let colorIndex = 0;
            
            edges.forEach(edge => {
                const value = edge[attribute] || edge.label || 'unknown';
                if (!edgeColors[value]) {
                    edgeColors[value] = palette[colorIndex % palette.length];
                    colorIndex++;
                }
            });
            
            const updates = edges.map(edge => {
                const value = edge[attribute] || edge.label || 'unknown';
                return {
                    id: edge.id,
                    color: { color: edgeColors[value], inherit: false }
                };
            });
            state.edges.update(updates);
        } else if (attribute === 'weight') {
            // Color by weight (gradient from light to dark)
            const weights = edges.map(e => e.weight || 1);
            const minWeight = Math.min(...weights);
            const maxWeight = Math.max(...weights);
            
            const updates = edges.map(edge => {
                const weight = edge.weight || 1;
                const normalized = (weight - minWeight) / (maxWeight - minWeight || 1);
                const intensity = Math.floor(100 + normalized * 155);
                return {
                    id: edge.id,
                    color: { color: `rgb(${intensity}, ${intensity}, 255)`, inherit: false }
                };
            });
            state.edges.update(updates);
        }
        
        state.currentEdgeColorBy = attribute;
    }

    function handleEdgeWidthChange(attribute) {
        console.log('[ExplorerWindows] Edge width by changed to:', attribute);
        const state = GraphExplorer?.getState();
        if (!state?.edges) return;

        const edges = state.edges.get();
        
        if (attribute === 'fixed') {
            // Reset to fixed width
            const updates = edges.map(edge => ({
                id: edge.id,
                width: 1
            }));
            state.edges.update(updates);
        } else if (attribute === 'weight') {
            // Width by edge weight
            const weights = edges.map(e => e.weight || 1);
            const minWeight = Math.min(...weights);
            const maxWeight = Math.max(...weights);
            
            const updates = edges.map(edge => {
                const weight = edge.weight || 1;
                const normalized = (weight - minWeight) / (maxWeight - minWeight || 1);
                const width = 1 + normalized * 5; // Range: 1-6
                return {
                    id: edge.id,
                    width: width
                };
            });
            state.edges.update(updates);
        } else if (attribute === 'count') {
            // Width by connection count (parallel edges)
            // This would need edge aggregation logic
            console.log('[ExplorerWindows] Edge width by count not yet implemented');
        }
        
        state.currentEdgeWidthBy = attribute;
    }

    function handleSettingsResetColors() {
        console.log('[ExplorerWindows] Resetting colors');
        if (typeof GraphExplorer !== 'undefined') {
            GraphExplorer.setColorByAttribute('resource_name');
            // Update select
            const select = document.getElementById('settings-color-select');
            if (select) select.value = 'resource_name';
            updateLegend();
        }
    }

    function handleSettingsResetSizes(win) {
        console.log('[ExplorerWindows] Resetting sizes');
        if (typeof GraphExplorer !== 'undefined') {
            GraphExplorer.setSizeScale(2);
        }
        // Update active button state
        win?.querySelectorAll('.settings-scale-btn').forEach(btn => {
            btn.classList.toggle('active', parseInt(btn.dataset.scale) === 2);
        });
    }

    // ============================================
    // Documents Handlers
    // ============================================
    function handleDocumentsSearch(e) {
        const query = e.target.value.toLowerCase();
        console.log('[ExplorerWindows] Documents search:', query);
        // TODO: Implement document search filtering
    }

    function handleDocumentsFilter(filter) {
        console.log('[ExplorerWindows] Documents filter:', filter);
        const listEl = document.getElementById('documents-list');
        if (!listEl) return;
        
        // Update active filter button
        document.querySelectorAll('.documents-filter-btn').forEach(btn => {
            btn.classList.toggle('active', btn.dataset.filter === filter);
        });
        
        // Filter rendered document items
        listEl.querySelectorAll('.document-item').forEach(item => {
            if (filter === 'all') {
                item.style.display = '';
            } else if (filter === 'linked') {
                item.style.display = parseInt(item.dataset.axiomCount || '0') > 0 ? '' : 'none';
            } else if (filter === 'unlinked') {
                item.style.display = parseInt(item.dataset.axiomCount || '0') === 0 ? '' : 'none';
            }
        });
    }

    async function handleLoadDocuments() {
        console.log('[ExplorerWindows] Loading documents...');
        if (typeof ExplorerAPI === 'undefined' || !ExplorerAPI.fetchGraphDocuments) {
            ExplorerUtils.announceChange('API not available');
            return;
        }
        
        const listEl = document.getElementById('documents-list');
        if (listEl) {
            safeSetInnerHTML(listEl, `
                <div class="window-empty">
                    <div class="icon">⏳</div>
                    <div class="text">Loading documents...</div>
                </div>
            `);
        }
        
        const data = await ExplorerAPI.fetchGraphDocuments();
        _renderDocumentsList(data);
    }

    /**
     * Render the documents list from API data.
     * Called by handleLoadDocuments and by updateDocumentsData (push from refreshGraph).
     */
    function _renderDocumentsList(data) {
        const listEl = document.getElementById('documents-list');
        const footerEl = document.getElementById('documents-footer');
        if (!listEl) return;
        
        if (!data || !data.documents || data.documents.length === 0) {
            safeSetInnerHTML(listEl, `
                <div class="window-empty">
                    <div class="icon">📄</div>
                    <div class="text">No documents found</div>
                    <button class="window-btn primary" id="load-documents-btn" style="margin-top: 12px;">Reload</button>
                </div>
            `);
            listEl.querySelector('#load-documents-btn')?.addEventListener('click', handleLoadDocuments);
            if (footerEl) footerEl.style.display = 'none';
            return;
        }
        
        const docs = data.documents;
        safeSetInnerHTML(listEl, docs.map(doc => {
            const name = escapeHtml(doc.name || doc.document_id || 'Untitled');
            const stream = escapeHtml(doc.stream_name || '');
            const axiomCount = doc.axiom_count || 0;
            const factCount = doc.fact_count || 0;
            return `
                <div class="document-item" data-document-id="${escapeHtml(doc.document_id || '')}" data-axiom-count="${axiomCount}">
                    <div class="document-info">
                        <i data-lucide="file-text" class="document-icon"></i>
                        <div class="document-details">
                            <span class="document-name">${name}</span>
                            ${stream ? `<span class="document-stream">${stream}</span>` : ''}
                        </div>
                    </div>
                    <div class="document-stats">
                        <span class="document-stat" title="Axioms">${axiomCount} axiom${axiomCount !== 1 ? 's' : ''}</span>
                        <span class="document-stat" title="Facts">${factCount} fact${factCount !== 1 ? 's' : ''}</span>
                    </div>
                </div>
            `;
        }).join(''));
        
        if (footerEl) {
            const countSpan = footerEl.querySelector('.documents-count');
            if (countSpan) {
                countSpan.textContent = `${docs.length} document${docs.length !== 1 ? 's' : ''}`;
            }
            footerEl.style.display = '';
        }
        
        if (typeof lucide !== 'undefined') {
            lucide.createIcons();
        }
    }

    /**
     * Called by ExplorerAPI._refreshSideData to push documents data
     * without requiring a manual button click.
     */
    function updateDocumentsData(data) {
        _renderDocumentsList(data);
    }

    function handleLinkDocuments() {
        console.log('[ExplorerWindows] Linking documents to selection...');
        // TODO: Implement document linking to selected nodes
        if (typeof GraphExplorer !== 'undefined') {
            ExplorerUtils.announceChange('Linking documents to selection...');
        }
    }

    // ============================================
    // FQL Editor Logic
    // ============================================
    
    // FQL Keywords for syntax highlighting
    const FQL_KEYWORDS = [
        'FIND', 'EXTEND', 'CONNECT', 'SELECT', 'MAP', 'EXTRACT', 'WHERE', 'AS',
        'CREATE', 'VIEW', 'RETURN', 'USE', 'IMPORT', 'SAVE', 'DROP', 'LIST',
        'CHECKPOINT', 'RESTORE', 'FILTER', 'ISOLATE', 'REMOVE', 'RUN',
        'TO', 'FROM', 'LIMIT', 'WITHIN', 'INCLUDE', 'COUNT', 'ON',
        'DIRECTION', 'OUT', 'IN', 'BOTH', 'MAX_HOPS',
        'AND', 'OR', 'NOT', 'TRUE', 'FALSE',
        'INTERSECT', 'UNION', 'DIFFERENCE'
    ];
    
    const FQL_ENTITIES = [
        'NODE', 'NODES', 'EDGE', 'EDGES', 'RESOURCE', 'RESOURCES',
        'NEIGHBORS', 'NEIGHBOR', 'STREAM', 'STREAMS',
        'DOCUMENT', 'DOCUMENTS', 'TAXONOMY', 'ONTOLOGY', 'ONTOLOGIES',
        'PATHS', 'INSTANCE', 'CURSOR', 'PATTERN', 'PATTERNS',
        'BOOKMARK', 'BOOKMARKS'
    ];
    
    const FQL_ALGORITHMS = [
        'ALGO', 'CENTRALITY', 'COMMUNITY', 'SIMILARITY', 'PATH', 'DAG', 'EMBEDDING', 'LINK_PREDICTION',
        'PAGERANK', 'PAGE_RANK', 'DEGREE', 'BETWEENNESS', 'CLOSENESS', 'EIGENVECTOR', 'HARMONIC',
        'LOUVAIN', 'LABEL_PROPAGATION', 'WCC', 'SCC', 'K_CORE', 'TRIANGLE_COUNT',
        'JACCARD', 'COSINE', 'OVERLAP', 'NODE_SIMILARITY', 'KNN',
        'BFS', 'DFS', 'DIJKSTRA', 'ASTAR', 'YEN', 'SHORTEST_PATH', 'ALL_SHORTEST_PATHS',
        'TOPOLOGICAL_SORT', 'LONGEST_PATH', 'ANCESTORS', 'DESCENDANTS',
        'FAST_RP', 'FASTRP', 'NODE2VEC', 'GRAPH_SAGE', 'HASH_GNN',
        'COMMON_NEIGHBORS', 'ADAMIC_ADAR', 'RESOURCE_ALLOCATION', 'PREFERENTIAL_ATTACHMENT'
    ];
    
    const FQL_FUNCTIONS = ['ALIAS', 'RESOURCE', 'PATTERN', 'FORMAT'];

    /**
     * Highlight FQL syntax in a contenteditable element
     */
    function highlightFqlEditor(editor) {
        // Save cursor position using selection
        const sel = window.getSelection();
        let cursorOffset = 0;
        
        if (sel.rangeCount > 0 && editor.contains(sel.anchorNode)) {
            const range = sel.getRangeAt(0);
            const preCaretRange = range.cloneRange();
            preCaretRange.selectNodeContents(editor);
            preCaretRange.setEnd(range.endContainer, range.endOffset);
            cursorOffset = preCaretRange.toString().length;
        }
        
        // Get plain text using textContent (most reliable way)
        let text = editor.textContent || editor.innerText || '';
        
        // Clean up any accidentally stored HTML tag strings
        text = text.replace(/<span[^>]*>/gi, '').replace(/<\/span>/gi, '');
        text = text.replace(/<br\s*\/?>/gi, '\n');
        
        if (!text.trim()) {
            safeSetInnerHTML(editor, '');
            return;
        }
        
        // Apply highlighting
        const highlighted = highlightFqlSyntax(text);
        safeSetInnerHTML(editor, highlighted);
        
        // Restore cursor position
        try {
            let charCount = 0;
            let found = false;
            const newRange = document.createRange();
            
            function findPosition(node) {
                if (found) return;
                
                if (node.nodeType === Node.TEXT_NODE) {
                    const nodeLength = node.textContent.length;
                    if (charCount + nodeLength >= cursorOffset) {
                        newRange.setStart(node, cursorOffset - charCount);
                        newRange.collapse(true);
                        found = true;
                        return;
                    }
                    charCount += nodeLength;
                } else {
                    for (const child of node.childNodes) {
                        findPosition(child);
                        if (found) return;
                    }
                }
            }
            
            findPosition(editor);
            
            if (found) {
                sel.removeAllRanges();
                sel.addRange(newRange);
            } else {
                // Put cursor at end
                newRange.selectNodeContents(editor);
                newRange.collapse(false);
                sel.removeAllRanges();
                sel.addRange(newRange);
            }
        } catch (e) {
            // Ignore cursor restore errors
        }
    }

    /**
     * Apply FQL syntax highlighting to text
     */
    function highlightFqlSyntax(text) {
        if (!text) return '';
        
        // Escape HTML first
        let result = text
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;');
        
        // Order matters! Process in order: comments, strings, then keywords
        
        // Comments (// and #)
        result = result.replace(/(\/\/.*$|#.*$)/gm, '<span class="fql-comment">$1</span>');
        
        // Strings (single and double quotes)
        result = result.replace(/("(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*')/g, '<span class="fql-string">$1</span>');
        
        // Variables ($name)
        result = result.replace(/(\$[a-zA-Z_][a-zA-Z0-9_]*)/g, '<span class="fql-variable">$1</span>');
        
        // Numbers
        result = result.replace(/\b(\d+\.?\d*)\b/g, '<span class="fql-number">$1</span>');
        
        // Algorithms (must come before keywords to catch compound names)
        const algoPattern = new RegExp(`\\b(${FQL_ALGORITHMS.join('|')})\\b`, 'gi');
        result = result.replace(algoPattern, '<span class="fql-algo">$1</span>');
        
        // Entities
        const entityPattern = new RegExp(`\\b(${FQL_ENTITIES.join('|')})\\b`, 'gi');
        result = result.replace(entityPattern, '<span class="fql-entity">$1</span>');
        
        // Functions (ALIAS(), RESOURCE(), etc.)
        const funcPattern = new RegExp(`\\b(${FQL_FUNCTIONS.join('|')})\\s*\\(`, 'gi');
        result = result.replace(funcPattern, '<span class="fql-function">$1</span>(');
        
        // Keywords (main FQL commands)
        const keywordPattern = new RegExp(`\\b(${FQL_KEYWORDS.join('|')})\\b`, 'gi');
        result = result.replace(keywordPattern, '<span class="fql-keyword">$1</span>');
        
        // Operators
        result = result.replace(/([=!<>]=?|&lt;&gt;|\|)/g, '<span class="fql-operator">$1</span>');
        
        // Preserve newlines
        result = result.replace(/\n/g, '<br>');
        
        return result;
    }

    /**
     * Get plain text from FQL editor
     */
    function getFqlEditorText() {
        const editor = document.getElementById('fql-editor');
        if (!editor) return '';
        
        // Use textContent to get plain text without any HTML
        let text = editor.textContent || editor.innerText || '';
        
        // Clean up any accidentally pasted/stored HTML tag strings
        // (happens when escaped HTML entities get decoded by textContent)
        text = text.replace(/<span[^>]*>/gi, '').replace(/<\/span>/gi, '');
        text = text.replace(/<br\s*\/?>/gi, '\n');
        
        return text;
    }

    /**
     * Set FQL editor text (plain text, no highlighting)
     */
    function setFqlEditorText(text) {
        const editor = document.getElementById('fql-editor');
        if (!editor) return;
        
        // Clean any HTML artifacts and set as plain text
        let cleanText = text
            .replace(/<span[^>]*>/gi, '')
            .replace(/<\/span>/gi, '')
            .replace(/<br\s*\/?>/gi, '\n');
        
        editor.textContent = cleanText;
    }

    // ============================================
    // Search Window - EntityInput Integration
    // ============================================
    
    // Store the EntityInput instance for the search window
    let searchWindowEntityInput = null;
    
    /**
     * Initialize the EntityInput in the search window
     */
    function initSearchWindowEntityInput(win) {
        const container = win.querySelector('#search-window-entity-input');
        const searchBtn = win.querySelector('#search-window-btn');
        const statusDiv = win.querySelector('#search-window-status');
        
        if (!container) {
            console.warn('[ExplorerWindows] Search window EntityInput container not found');
            return;
        }
        
        // Check if EntityInput is available
        if (typeof EntityInput === 'undefined') {
            safeSetInnerHTML(container, '<div class="window-empty">EntityInput not available</div>');
            console.warn('[ExplorerWindows] EntityInput class not available');
            return;
        }
        
        // Create EntityInput instance
        // Detect theme from graph-explorer element (light-theme class) or fall back to dark
        const graphExplorer = document.querySelector('.graph-explorer');
        const isLightTheme = graphExplorer?.classList.contains('light-theme');
        const theme = isLightTheme ? 'light' : 'dark';
        searchWindowEntityInput = new EntityInput({
            placeholder: 'Search nodes, concepts, relationships...',
            debounceMs: 300,
            minSearchLength: 2,
            theme: theme,
            dropdownPosition: 'below',
            onSearch: (query) => handleSearchWindowAutocomplete(query),
            onSubmit: (data) => {
                const query = data.query;
                const keywords = data.keywords || [];
                if (query || keywords.length > 0) {
                    executeSearchWindowQuery(query, keywords, statusDiv);
                }
            },
            onEntitySelect: (entity) => {
                console.log('[ExplorerWindows] Search entity selected:', entity);
            },
            onChange: (data) => {
                // Optional: track changes
            },
        });
        
        searchWindowEntityInput.init(container);
        
        // Re-init lucide icons
        if (typeof lucide !== 'undefined') {
            lucide.createIcons();
        }
        
        // Bind search button
        if (searchBtn) {
            searchBtn.addEventListener('click', () => {
                if (!searchWindowEntityInput) return;
                
                const query = searchWindowEntityInput.getFullQuery();
                const keywords = searchWindowEntityInput.getSelectedEntities();
                
                if (query || keywords.length > 0) {
                    executeSearchWindowQuery(query, keywords, statusDiv);
                } else {
                    if (statusDiv) {
                        safeSetInnerHTML(statusDiv, '<span class="search-hint">Enter a search query or select entities</span>');
                    }
                }
            });
        }
    }
    
    /**
     * Handle autocomplete for search window
     */
    async function handleSearchWindowAutocomplete(query) {
        if (!query || query.length < 2) {
            if (searchWindowEntityInput) {
                searchWindowEntityInput.setSuggestions([]);
            }
            return;
        }
        
        // Get API config from ExplorerAPI
        if (typeof ExplorerAPI === 'undefined' || !ExplorerAPI.isConfigured || !ExplorerAPI.isConfigured()) {
            return;
        }
        
        const config = ExplorerAPI.getConfig ? ExplorerAPI.getConfig() : {};
        const apiEndpoint = config.apiEndpoint || '';
        const workspaceId = config.workspaceId;
        
        if (!workspaceId) {
            return;
        }
        
        try {
            const url = `${apiEndpoint}/workspaces/${workspaceId}/search/suggest?query=${encodeURIComponent(query)}&limit=8`;
            const headers = {
                'Accept': 'application/json'
            };
            
            // Add auth header if available
            if (ExplorerAPI.getAuthHeaders) {
                Object.assign(headers, ExplorerAPI.getAuthHeaders());
            }
            
            const fetchOptions = { method: 'GET', headers };
            if (ExplorerAPI.shouldIncludeCredentials && ExplorerAPI.shouldIncludeCredentials()) {
                fetchOptions.credentials = 'include';
            }
            
            const response = await fetch(url, fetchOptions);
            
            if (response.ok) {
                const suggestions = await response.json();
                if (searchWindowEntityInput && suggestions && suggestions.length > 0) {
                    searchWindowEntityInput.setSuggestions(suggestions);
                } else if (searchWindowEntityInput) {
                    searchWindowEntityInput.setSuggestions([]);
                }
            }
        } catch (err) {
            console.warn('[ExplorerWindows] Failed to load search suggestions:', err);
            if (searchWindowEntityInput) {
                searchWindowEntityInput.setSuggestions([]);
            }
        }
    }
    
    /**
     * Execute search query and load results into graph
     */
    async function executeSearchWindowQuery(query, keywords, statusDiv) {
        if (typeof ExplorerAPI === 'undefined' || !ExplorerAPI.searchAsk) {
            if (statusDiv) {
                safeSetInnerHTML(statusDiv, '<span class="search-error">API not available</span>');
            }
            return;
        }
        
        // Show loading state
        if (statusDiv) {
            safeSetInnerHTML(statusDiv, '<span class="search-loading"><i data-lucide="loader-2" class="spin"></i> Searching...</span>');
            if (typeof lucide !== 'undefined') lucide.createIcons();
        }
        
        try {
            const result = await ExplorerAPI.searchAsk(query, keywords, { resetCursor: false });
            console.log('[ExplorerWindows] Search result:', result);
            
            const loaded = result.total_entities_loaded || 0;
            if (statusDiv) {
                if (loaded > 0) {
                    safeSetInnerHTML(statusDiv, `<span class="search-success"><i data-lucide="check-circle"></i> Loaded ${loaded} entities</span>`);
                } else {
                    safeSetInnerHTML(statusDiv, '<span class="search-hint">No matching entities found</span>');
                }
                if (typeof lucide !== 'undefined') lucide.createIcons();
            }
            
            // Refresh the graph to show new nodes
            if (ExplorerAPI.refreshGraph) {
                await ExplorerAPI.refreshGraph();
            }
            
            // Update aliases list
            if (ExplorerAPI.fetchAliases) {
                const aliases = await ExplorerAPI.fetchAliases();
                if (ExplorerAPI.updateAliasesList) {
                    ExplorerAPI.updateAliasesList(aliases);
                }
            }
            
            // Clear the input after successful search
            if (searchWindowEntityInput) {
                searchWindowEntityInput.clear();
            }
            
        } catch (err) {
            console.error('[ExplorerWindows] Search failed:', err);
            if (statusDiv) {
                safeSetInnerHTML(statusDiv, `<span class="search-error"><i data-lucide="alert-circle"></i> ${escapeHtml(err.message)}</span>`);
                if (typeof lucide !== 'undefined') lucide.createIcons();
            }
        }
    }

    // ============================================
    // Checkpoints & Bookmarks Window
    // ============================================
    
    /**
     * Initialize the checkpoints & bookmarks window
     */
    function initCheckpointsWindow(win) {
        // Tab switching
        win.querySelectorAll('.checkpoints-tab').forEach(tab => {
            tab.addEventListener('click', function() {
                const tabId = this.dataset.tab;
                switchCheckpointsTab(win, tabId);
            });
        });
        
        // Create checkpoint button
        const checkpointCreateBtn = win.querySelector('#checkpoint-create-btn');
        const checkpointNameInput = win.querySelector('#checkpoint-name-input');
        
        checkpointCreateBtn?.addEventListener('click', () => {
            const name = checkpointNameInput?.value.trim();
            if (name) {
                createCheckpoint(name);
                checkpointNameInput.value = '';
            }
        });
        
        checkpointNameInput?.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                const name = checkpointNameInput.value.trim();
                if (name) {
                    createCheckpoint(name);
                    checkpointNameInput.value = '';
                }
            }
        });
        
        // Create bookmark button
        const bookmarkCreateBtn = win.querySelector('#bookmark-create-btn');
        const bookmarkNameInput = win.querySelector('#bookmark-name-input');
        
        bookmarkCreateBtn?.addEventListener('click', () => {
            const name = bookmarkNameInput?.value.trim();
            if (name) {
                createBookmark(name);
                bookmarkNameInput.value = '';
            }
        });
        
        bookmarkNameInput?.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                const name = bookmarkNameInput.value.trim();
                if (name) {
                    createBookmark(name);
                    bookmarkNameInput.value = '';
                }
            }
        });
        
        // Re-init lucide icons
        if (typeof lucide !== 'undefined') {
            lucide.createIcons();
        }
        
        // Load initial data
        loadCheckpointsAndBookmarks();
    }
    
    /**
     * Switch between checkpoints and bookmarks tabs
     */
    function switchCheckpointsTab(win, tabId) {
        // Update tab buttons
        win.querySelectorAll('.checkpoints-tab').forEach(tab => {
            tab.classList.toggle('active', tab.dataset.tab === tabId);
        });
        
        // Update content panels
        win.querySelectorAll('.checkpoints-panel').forEach(panel => {
            panel.style.display = panel.dataset.panel === tabId ? 'block' : 'none';
        });
        
        // Update content container attribute
        const content = win.querySelector('#checkpoints-tab-content');
        if (content) {
            content.dataset.activeTab = tabId;
        }
    }
    
    /**
     * Load checkpoints and bookmarks from cursor state
     */
    async function loadCheckpointsAndBookmarks() {
        if (typeof ExplorerAPI === 'undefined' || !ExplorerAPI.isConfigured || !ExplorerAPI.isConfigured()) {
            return;
        }
        
        try {
            const config = ExplorerAPI.getConfig ? ExplorerAPI.getConfig() : {};
            const apiEndpoint = config.apiEndpoint || '';
            const workspaceId = config.workspaceId;
            const cursorId = config.cursorId || 'me';
            
            if (!workspaceId) return;
            
            const url = `${apiEndpoint}/workspaces/${workspaceId}/cursors/${cursorId}/state`;
            const headers = { 'Accept': 'application/json' };
            
            if (ExplorerAPI.getAuthHeaders) {
                Object.assign(headers, ExplorerAPI.getAuthHeaders());
            }
            
            const fetchOptions = { method: 'GET', headers };
            if (ExplorerAPI.shouldIncludeCredentials && ExplorerAPI.shouldIncludeCredentials()) {
                fetchOptions.credentials = 'include';
            }
            
            const response = await fetch(url, fetchOptions);
            
            if (response.ok) {
                const state = await response.json();
                // checkpoints is a dict with name as key
                const checkpointsDict = state.checkpoints || {};
                const checkpointsArray = Object.keys(checkpointsDict).map(name => ({
                    name: name,
                    ...checkpointsDict[name]
                }));
                updateCheckpointsList(checkpointsArray);
                
                // bookmarks could be either array or dict format
                const bookmarks = state.bookmarks;
                if (Array.isArray(bookmarks)) {
                    updateBookmarksList(bookmarks);
                } else if (bookmarks && typeof bookmarks === 'object') {
                    const bookmarksArray = Object.keys(bookmarks).map(name => ({
                        name: name,
                        ...bookmarks[name]
                    }));
                    updateBookmarksList(bookmarksArray);
                } else {
                    updateBookmarksList([]);
                }
            }
        } catch (err) {
            console.warn('[ExplorerWindows] Failed to load checkpoints/bookmarks:', err);
        }
    }

    /**
     * Update checkpoints and bookmarks from pre-fetched state data.
     * Called by ExplorerAPI._refreshSideData after graph loads.
     */
    function updateStateData(state) {
        if (!state) return;
        try {
            // Handle checkpoints as array or dict
            const checkpointsRaw = state.checkpoints || [];
            let checkpointsArray;
            if (Array.isArray(checkpointsRaw)) {
                checkpointsArray = checkpointsRaw;
            } else if (typeof checkpointsRaw === 'object') {
                checkpointsArray = Object.keys(checkpointsRaw).map(name => ({
                    name: name,
                    ...checkpointsRaw[name]
                }));
            } else {
                checkpointsArray = [];
            }
            updateCheckpointsList(checkpointsArray);

            const bookmarks = state.bookmarks;
            if (Array.isArray(bookmarks)) {
                updateBookmarksList(bookmarks);
            } else if (bookmarks && typeof bookmarks === 'object') {
                const bookmarksArray = Object.keys(bookmarks).map(name => ({
                    name: name,
                    ...bookmarks[name]
                }));
                updateBookmarksList(bookmarksArray);
            } else {
                updateBookmarksList([]);
            }
        } catch (err) {
            console.warn('[ExplorerWindows] updateStateData error:', err);
        }
    }
    
    /**
     * Clear checkpoints and bookmarks lists (called after cursor reset)
     */
    function clearCheckpointsAndBookmarks() {
        updateCheckpointsList([]);
        updateBookmarksList([]);
        console.log('[ExplorerWindows] Cleared checkpoints and bookmarks');
    }
    
    /**
     * Update the checkpoints list UI
     */
    function updateCheckpointsList(checkpoints) {
        const list = document.getElementById('checkpoints-list');
        if (!list) return;
        
        if (!checkpoints || checkpoints.length === 0) {
            safeSetInnerHTML(list, `
                <div class="window-empty">
                    <i data-lucide="flag"></i>
                    <span>No checkpoints yet</span>
                </div>
            `);
        } else {
            safeSetInnerHTML(list, checkpoints.map((cp, idx) => {
                const axioms = cp.axiom_count != null ? cp.axiom_count : '';
                const docs = cp.document_count != null ? cp.document_count : '';
                const statsHtml = (axioms !== '' || docs !== '') ? `
                    <div class="checkpoint-stats" style="font-size:11px;opacity:0.7;margin-left:22px;">
                        ${docs !== '' ? `<span>${docs} doc${docs !== 1 ? 's' : ''}</span>` : ''}
                        ${axioms !== '' ? `<span style="margin-left:6px;">${axioms} axiom${axioms !== 1 ? 's' : ''}</span>` : ''}
                    </div>` : '';
                return `
                <div class="checkpoint-item" data-checkpoint="${escapeHtml(cp.name || `checkpoint_${idx}`)}">
                    <div class="checkpoint-info">
                        <i data-lucide="flag" class="checkpoint-icon"></i>
                        <span class="checkpoint-name">${escapeHtml(cp.name || `Checkpoint ${idx + 1}`)}</span>
                    </div>${statsHtml}
                    <div class="checkpoint-actions">
                        <button class="checkpoint-action-btn" data-action="preview" title="Preview this checkpoint (load graph without restoring)">
                            <i data-lucide="eye"></i>
                        </button>
                        <button class="checkpoint-action-btn" data-action="restore" title="Restore this checkpoint">
                            <i data-lucide="undo-2"></i>
                        </button>
                        <button class="checkpoint-action-btn danger" data-action="delete" title="Delete this checkpoint">
                            <i data-lucide="trash-2"></i>
                        </button>
                    </div>
                </div>
            `}).join(''));
            
            // Bind click handlers
            list.querySelectorAll('.checkpoint-item').forEach(item => {
                const name = item.dataset.checkpoint;
                item.querySelector('[data-action="preview"]')?.addEventListener('click', (e) => {
                    e.stopPropagation();
                    previewPointer('checkpoint', name);
                });
                item.querySelector('[data-action="restore"]')?.addEventListener('click', (e) => {
                    e.stopPropagation();
                    restoreCheckpoint(name);
                });
                item.querySelector('[data-action="delete"]')?.addEventListener('click', (e) => {
                    e.stopPropagation();
                    deleteCheckpoint(name);
                });
            });
        }
        
        if (typeof lucide !== 'undefined') {
            lucide.createIcons();
        }
    }
    
    /**
     * Update the bookmarks list UI
     */
    function updateBookmarksList(bookmarks) {
        const list = document.getElementById('bookmarks-list');
        if (!list) return;
        
        if (!bookmarks || bookmarks.length === 0) {
            safeSetInnerHTML(list, `
                <div class="window-empty">
                    <i data-lucide="bookmark"></i>
                    <span>No bookmarks yet</span>
                </div>
            `);
        } else {
            safeSetInnerHTML(list, bookmarks.map((bm, idx) => {
                const axioms = bm.axiom_count != null ? bm.axiom_count : '';
                const docs = bm.document_count != null ? bm.document_count : '';
                const statsHtml = (axioms !== '' || docs !== '') ? `
                    <div class="bookmark-stats" style="font-size:11px;opacity:0.7;margin-left:22px;">
                        ${docs !== '' ? `<span>${docs} doc${docs !== 1 ? 's' : ''}</span>` : ''}
                        ${axioms !== '' ? `<span style="margin-left:6px;">${axioms} axiom${axioms !== 1 ? 's' : ''}</span>` : ''}
                    </div>` : '';
                return `
                <div class="bookmark-item" data-bookmark="${escapeHtml(bm.name || `bookmark_${idx}`)}">
                    <div class="bookmark-info">
                        <i data-lucide="bookmark" class="bookmark-icon"></i>
                        <span class="bookmark-name">${escapeHtml(bm.name || `Bookmark ${idx + 1}`)}</span>
                    </div>${statsHtml}
                    <div class="bookmark-actions">
                        <button class="bookmark-action-btn" data-action="preview" title="Preview this bookmark (load graph without using)">
                            <i data-lucide="eye"></i>
                        </button>
                        <button class="bookmark-action-btn" data-action="use" title="Use this bookmark">
                            <i data-lucide="play"></i>
                        </button>
                        <button class="bookmark-action-btn danger" data-action="delete" title="Delete this bookmark">
                            <i data-lucide="trash-2"></i>
                        </button>
                    </div>
                </div>
            `}).join(''));
            
            // Bind click handlers
            list.querySelectorAll('.bookmark-item').forEach(item => {
                const name = item.dataset.bookmark;
                item.querySelector('[data-action="preview"]')?.addEventListener('click', (e) => {
                    e.stopPropagation();
                    previewPointer('bookmark', name);
                });
                item.querySelector('[data-action="use"]')?.addEventListener('click', (e) => {
                    e.stopPropagation();
                    useBookmark(name);
                });
                item.querySelector('[data-action="delete"]')?.addEventListener('click', (e) => {
                    e.stopPropagation();
                    deleteBookmark(name);
                });
            });
        }
        
        if (typeof lucide !== 'undefined') {
            lucide.createIcons();
        }
    }
    
    /**
     * Create a new checkpoint
     */
    async function createCheckpoint(name) {
        const statusDiv = document.getElementById('checkpoint-status');
        
        if (typeof ExplorerAPI === 'undefined' || !ExplorerAPI.executeMQL) {
            showCheckpointStatus('API not available', 'error');
            return;
        }
        
        showCheckpointStatus('Creating checkpoint...', 'loading');
        
        try {
            // Execute FQL to create checkpoint
            const query = `CHECKPOINT('${name.replace(/'/g, "''")}');`;
            await ExplorerAPI.executeMQL(query);
            
            showCheckpointStatus(`Checkpoint "${name}" created`, 'success');
            
            // Refresh the list
            await loadCheckpointsAndBookmarks();
            
            // Also refresh FQL history
            if (typeof loadFqlHistory === 'function') {
                loadFqlHistory();
            }
            
        } catch (err) {
            console.error('[ExplorerWindows] Failed to create checkpoint:', err);
            showCheckpointStatus(`Failed: ${err.message}`, 'error');
        }
    }
    
    /**
     * Preview a checkpoint or bookmark — load its graph/data into the canvas
     * WITHOUT modifying the cursor's live state.
     * @param {'checkpoint'|'bookmark'} pointerType
     * @param {string} name
     */
    async function previewPointer(pointerType, name) {
        if (typeof ExplorerAPI === 'undefined' || !ExplorerAPI.fetchGraphDataForPointer) {
            showCheckpointStatus('API not available', 'error');
            return;
        }
        
        const label = pointerType === 'checkpoint' ? 'checkpoint' : 'bookmark';
        showCheckpointStatus(`Loading ${label} "${name}"...`, 'loading');
        
        try {
            const graphData = await ExplorerAPI.fetchGraphDataForPointer(pointerType, name);
            if (graphData && !graphData.error) {
                ExplorerAPI.updateGraph(graphData, true);
                showCheckpointStatus(
                    `Previewing ${label} "${name}" (${graphData.nodes?.length || 0} nodes)`,
                    'success'
                );
            } else {
                showCheckpointStatus(`Failed: ${graphData?.error || 'Unknown error'}`, 'error');
            }
        } catch (err) {
            console.error(`[ExplorerWindows] Failed to preview ${pointerType}:`, err);
            showCheckpointStatus(`Failed: ${err.message}`, 'error');
        }
    }

    /**
     * Restore a checkpoint
     */
    async function restoreCheckpoint(name) {
        const statusDiv = document.getElementById('checkpoint-status');
        
        if (typeof ExplorerAPI === 'undefined' || !ExplorerAPI.executeMQL) {
            showCheckpointStatus('API not available', 'error');
            return;
        }
        
        showCheckpointStatus('Restoring checkpoint...', 'loading');
        
        try {
            // Execute FQL to restore checkpoint
            const query = `RESTORE('${name.replace(/'/g, "''")}');`;
            await ExplorerAPI.executeMQL(query);
            
            showCheckpointStatus(`Restored to "${name}"`, 'success');
            
            // Refresh the graph
            if (ExplorerAPI.refreshGraph) {
                await ExplorerAPI.refreshGraph();
            }
            
            // Refresh FQL history
            if (typeof loadFqlHistory === 'function') {
                loadFqlHistory();
            }
            
        } catch (err) {
            console.error('[ExplorerWindows] Failed to restore checkpoint:', err);
            showCheckpointStatus(`Failed: ${err.message}`, 'error');
        }
    }
    
    /**
     * Delete a checkpoint (not implemented in FQL - would need API endpoint)
     */
    async function deleteCheckpoint(name) {
        showCheckpointStatus('Delete not yet implemented', 'info');
    }
    
    /**
     * Create a new bookmark
     */
    async function createBookmark(name) {
        const statusDiv = document.getElementById('checkpoint-status');
        
        if (typeof ExplorerAPI === 'undefined' || !ExplorerAPI.executeMQL) {
            showCheckpointStatus('API not available', 'error');
            return;
        }
        
        showCheckpointStatus('Creating bookmark...', 'loading');
        
        try {
            // Execute FQL to create bookmark
            const query = `CREATE BOOKMARK('${name.replace(/'/g, "''")}');`;
            await ExplorerAPI.executeMQL(query);
            
            showCheckpointStatus(`Bookmark "${name}" created`, 'success');
            
            // Refresh the list and switch to bookmarks tab
            await loadCheckpointsAndBookmarks();
            
            // Switch to bookmarks tab
            const win = document.querySelector('[data-window-id="checkpoints"]');
            if (win) {
                switchCheckpointsTab(win, 'bookmarks');
            }
            
            // Also refresh FQL history
            if (typeof loadFqlHistory === 'function') {
                loadFqlHistory();
            }
            
        } catch (err) {
            console.error('[ExplorerWindows] Failed to create bookmark:', err);
            showCheckpointStatus(`Failed: ${err.message}`, 'error');
        }
    }
    
    /**
     * Use a bookmark
     */
    async function useBookmark(name) {
        const statusDiv = document.getElementById('checkpoint-status');
        
        if (typeof ExplorerAPI === 'undefined' || !ExplorerAPI.executeMQL) {
            showCheckpointStatus('API not available', 'error');
            return;
        }
        
        showCheckpointStatus('Loading bookmark...', 'loading');
        
        try {
            // Execute FQL to use bookmark
            const query = `USE BOOKMARK('${name.replace(/'/g, "''")}');`;
            await ExplorerAPI.executeMQL(query);
            
            showCheckpointStatus(`Loaded bookmark "${name}"`, 'success');
            
            // Refresh the graph
            if (ExplorerAPI.refreshGraph) {
                await ExplorerAPI.refreshGraph();
            }
            
            // Refresh FQL history
            if (typeof loadFqlHistory === 'function') {
                loadFqlHistory();
            }
            
        } catch (err) {
            console.error('[ExplorerWindows] Failed to use bookmark:', err);
            showCheckpointStatus(`Failed: ${err.message}`, 'error');
        }
    }
    
    /**
     * Delete a bookmark (not implemented in FQL - would need API endpoint)
     */
    async function deleteBookmark(name) {
        showCheckpointStatus('Delete not yet implemented', 'info');
    }
    
    /**
     * Show status message in checkpoints window
     */
    function showCheckpointStatus(message, type = 'info') {
        const statusDiv = document.getElementById('checkpoint-status');
        if (!statusDiv) return;
        
        const iconMap = {
            loading: 'loader-2',
            success: 'check-circle',
            error: 'alert-circle',
            info: 'info'
        };
        
        const icon = iconMap[type] || 'info';
        const spinClass = type === 'loading' ? ' class="spin"' : '';
        
        safeSetInnerHTML(statusDiv, `
            <span class="checkpoint-status-${type}">
                <i data-lucide="${icon}"${spinClass}></i>
                ${escapeHtml(message)}
            </span>
        `);
        
        if (typeof lucide !== 'undefined') {
            lucide.createIcons();
        }
        
        // Auto-clear success/info messages after 3 seconds
        if (type === 'success' || type === 'info') {
            setTimeout(() => {
                if (statusDiv.innerHTML.includes(message)) {
                    safeSetInnerHTML(statusDiv, '');
                }
            }, 3000);
        }
    }

    // ============================================
    // Patterns Window
    // ============================================
    
    /**
     * Initialize the patterns window
     */
    function initPatternsWindow(win) {
        // Search input
        const searchInput = win.querySelector('#patterns-search-input');
        searchInput?.addEventListener('input', function() {
            filterPatternsList(this.value);
        });
        
        // Re-init lucide icons
        if (typeof lucide !== 'undefined') {
            lucide.createIcons();
        }
        
        // Load patterns
        loadPatterns();
    }
    
    /**
     * Load patterns from API
     */
    async function loadPatterns() {
        const list = document.getElementById('patterns-list');
        if (!list) return;
        
        if (typeof ExplorerAPI === 'undefined' || !ExplorerAPI.isConfigured || !ExplorerAPI.isConfigured()) {
            safeSetInnerHTML(list, `
                <div class="window-empty">
                    <i data-lucide="alert-circle"></i>
                    <span>API not configured</span>
                </div>
            `);
            if (typeof lucide !== 'undefined') lucide.createIcons();
            return;
        }
        
        try {
            const config = ExplorerAPI.getConfig ? ExplorerAPI.getConfig() : {};
            const apiEndpoint = config.apiEndpoint || '';
            const workspaceId = config.workspaceId;
            
            if (!workspaceId) {
                safeSetInnerHTML(list, `
                    <div class="window-empty">
                        <i data-lucide="alert-circle"></i>
                        <span>No workspace selected</span>
                    </div>
                `);
                if (typeof lucide !== 'undefined') lucide.createIcons();
                return;
            }
            
            const url = `${apiEndpoint}/workspaces/${workspaceId}/patterns/`;
            const headers = { 'Accept': 'application/json' };
            
            if (ExplorerAPI.getAuthHeaders) {
                Object.assign(headers, ExplorerAPI.getAuthHeaders());
            }
            
            const response = await fetch(url, { method: 'GET', headers, credentials: 'include' });
            
            if (response.ok) {
                const patterns = await response.json();
                renderPatternsList(patterns);
            } else {
                safeSetInnerHTML(list, `
                    <div class="window-empty">
                        <i data-lucide="alert-circle"></i>
                        <span>Failed to load patterns</span>
                    </div>
                `);
                if (typeof lucide !== 'undefined') lucide.createIcons();
            }
        } catch (err) {
            console.warn('[ExplorerWindows] Failed to load patterns:', err);
            safeSetInnerHTML(list, `
                <div class="window-empty">
                    <i data-lucide="alert-circle"></i>
                    <span>Error loading patterns</span>
                </div>
            `);
            if (typeof lucide !== 'undefined') lucide.createIcons();
        }
    }
    
    /**
     * Render the patterns list
     */
    function renderPatternsList(patterns) {
        const list = document.getElementById('patterns-list');
        if (!list) return;
        
        if (!patterns || patterns.length === 0) {
            safeSetInnerHTML(list, `
                <div class="window-empty">
                    <i data-lucide="shapes"></i>
                    <span>No patterns available</span>
                </div>
            `);
            if (typeof lucide !== 'undefined') lucide.createIcons();
            return;
        }
        
        safeSetInnerHTML(list, patterns.map(pattern => `
            <div class="pattern-item" data-pattern-id="${escapeHtml(pattern.id || pattern.name)}">
                <div class="pattern-info">
                    <i data-lucide="shapes" class="pattern-icon"></i>
                    <div class="pattern-details">
                        <span class="pattern-name">${escapeHtml(pattern.name)}</span>
                        ${pattern.description ? `<span class="pattern-description">${escapeHtml(pattern.description)}</span>` : ''}
                        ${pattern.statements_count ? `<span class="pattern-statements-count">${pattern.statements_count} statement${pattern.statements_count !== 1 ? 's' : ''}</span>` : ''}
                    </div>
                </div>
                <div class="pattern-actions">
                    <button class="pattern-action-btn" data-action="use" title="Use this pattern">
                        <i data-lucide="play"></i>
                    </button>
                    <button class="pattern-action-btn" data-action="copy" title="Copy to FQL Editor">
                        <i data-lucide="copy"></i>
                    </button>
                </div>
            </div>
        `).join(''));
        
        // Bind click handlers
        list.querySelectorAll('.pattern-item').forEach(item => {
            const patternId = item.dataset.patternId;
            
            item.querySelector('[data-action="use"]')?.addEventListener('click', (e) => {
                e.stopPropagation();
                usePattern(patternId);
            });
            
            item.querySelector('[data-action="copy"]')?.addEventListener('click', (e) => {
                e.stopPropagation();
                copyPatternToFqlEditor(patternId);
            });
        });
        
        if (typeof lucide !== 'undefined') lucide.createIcons();
    }
    
    /**
     * Filter patterns list by search term
     */
    function filterPatternsList(searchTerm) {
        const list = document.getElementById('patterns-list');
        if (!list) return;
        
        const items = list.querySelectorAll('.pattern-item');
        const term = searchTerm.toLowerCase();
        
        items.forEach(item => {
            const name = item.querySelector('.pattern-name')?.textContent.toLowerCase() || '';
            const desc = item.querySelector('.pattern-description')?.textContent.toLowerCase() || '';
            const matches = name.includes(term) || desc.includes(term);
            item.style.display = matches ? '' : 'none';
        });
    }
    
    /**
     * Fetch full pattern details by ID
     */
    async function fetchPatternDetails(patternId) {
        if (typeof ExplorerAPI === 'undefined' || !ExplorerAPI.isConfigured || !ExplorerAPI.isConfigured()) {
            console.error('[ExplorerWindows] API not configured');
            return null;
        }
        
        try {
            const config = ExplorerAPI.getConfig ? ExplorerAPI.getConfig() : {};
            const apiEndpoint = config.apiEndpoint || '';
            const workspaceId = config.workspaceId;
            
            if (!workspaceId) {
                console.error('[ExplorerWindows] No workspace selected');
                return null;
            }
            
            const url = `${apiEndpoint}/workspaces/${workspaceId}/patterns/${patternId}`;
            const headers = { 'Accept': 'application/json' };
            
            if (ExplorerAPI.getAuthHeaders) {
                Object.assign(headers, ExplorerAPI.getAuthHeaders());
            }

            const fetchOptions = { method: 'GET', headers };
            // Include credentials for cookie-based auth (MCP UI sessions)
            if (ExplorerAPI.shouldIncludeCredentials && ExplorerAPI.shouldIncludeCredentials()) {
                fetchOptions.credentials = 'include';
            }
            
            const response = await fetch(url, fetchOptions);
            
            if (response.ok) {
                return await response.json();
            } else {
                console.error('[ExplorerWindows] Failed to fetch pattern:', response.status);
                return null;
            }
        } catch (err) {
            console.error('[ExplorerWindows] Error fetching pattern:', err);
            return null;
        }
    }
    
    /**
     * Use a pattern (execute USE PATTERN command)
     */
    async function usePattern(patternId) {
        if (!patternId) return;
        
        // Fetch pattern details to get the name
        const pattern = await fetchPatternDetails(patternId);
        if (!pattern || !pattern.name) {
            console.error('[ExplorerWindows] Pattern not found or has no name');
            if (typeof ExplorerAPI !== 'undefined' && ExplorerAPI.showWarningNotification) {
                ExplorerAPI.showWarningNotification('Could not load pattern. Please try again.');
            }
            return;
        }
        
        // Clear the cursor first so old data doesn't accumulate under the
        // new pattern results, then apply the pattern in a single MQL batch.
        const query = `CLEAR;\nUSE PATTERN('${pattern.name}');`;
        
        if (typeof ExplorerAPI === 'undefined' || !ExplorerAPI.executeMQL) {
            console.error('[ExplorerWindows] API not available');
            return;
        }
        
        try {
            // Clear vis.js nodes/edges immediately for visual feedback
            if (typeof GraphExplorer !== 'undefined' && GraphExplorer.state) {
                if (GraphExplorer.state.nodes) GraphExplorer.state.nodes.clear();
                if (GraphExplorer.state.edges) GraphExplorer.state.edges.clear();
            }

            await ExplorerAPI.executeMQL(query);
            
            // Refresh the graph
            if (ExplorerAPI.refreshGraph) {
                await ExplorerAPI.refreshGraph();
            }
            
            // Refresh FQL history
            if (typeof loadFqlHistory === 'function') {
                loadFqlHistory();
            }
        } catch (err) {
            console.error('[ExplorerWindows] Failed to use pattern:', err);
            if (typeof ExplorerAPI !== 'undefined' && ExplorerAPI.showWarningNotification) {
                ExplorerAPI.showWarningNotification('Failed to apply pattern: ' + (err.message || 'Unknown error'));
            }
        }
    }
    
    /**
     * Copy pattern statements to FQL Editor
     */
    async function copyPatternToFqlEditor(patternId) {
        if (!patternId) return;
        
        // Fetch full pattern details
        const pattern = await fetchPatternDetails(patternId);
        if (!pattern || !pattern.statements || pattern.statements.length === 0) {
            console.error('[ExplorerWindows] Pattern not found or has no statements');
            return;
        }
        
        // Join statements with newline
        const query = pattern.statements.join('\n');
        
        // Open FQL window if not open
        open('fql');
        
        // Set the query in the editor
        setTimeout(() => {
            setFqlEditorText(query);
        }, 100);
    }

    /**
     * Handle FQL execute button click
     */
    async function handleFqlExecute() {
        const query = getFqlEditorText().trim();
        if (!query) {
            showFqlError('Please enter an FQL query');
            return;
        }
        
        console.log('[ExplorerWindows] Executing FQL:', query);
        showFqlLoading(true);
        
        try {
            if (typeof ExplorerAPI === 'undefined') {
                throw new Error('ExplorerAPI not available');
            }
            
            if (!ExplorerAPI.isConfigured || !ExplorerAPI.isConfigured()) {
                throw new Error('API not configured. Please select a workspace first.');
            }
            
            if (!ExplorerAPI.executeMQL) {
                throw new Error('MQL execution not available');
            }
            
            const result = await ExplorerAPI.executeMQL(query);
            
            // Show success result
            showFqlResult(result);
            
            // Refresh the graph to show changes
            if (ExplorerAPI.refreshGraph) {
                await ExplorerAPI.refreshGraph(true);
            }
            
            // Reload history
            loadFqlHistory();
            
            if (typeof ExplorerUtils !== 'undefined' && ExplorerUtils.announceChange) {
                ExplorerUtils.announceChange('FQL query executed successfully');
            }
        } catch (error) {
            console.error('[ExplorerWindows] FQL execution error:', error);
            showFqlError(error.message || 'Failed to execute FQL query');
        } finally {
            showFqlLoading(false);
        }
    }

    /**
     * Show FQL result
     */
    function showFqlResult(result) {
        const section = document.getElementById('fql-result-section');
        const resultEl = document.getElementById('fql-result');
        
        if (section && resultEl) {
            section.style.display = 'block';
            resultEl.className = 'fql-result success';
            
            // Format the result
            let display = '';
            if (result && typeof result === 'object') {
                if (result.message) {
                    display = result.message;
                } else if (result.nodes_added !== undefined || result.edges_added !== undefined) {
                    display = `Added: ${result.nodes_added || 0} nodes, ${result.edges_added || 0} edges`;
                } else if (result.count !== undefined) {
                    display = `Result count: ${result.count}`;
                } else {
                    display = JSON.stringify(result, null, 2);
                }
            } else {
                display = String(result || 'Query executed');
            }
            
            resultEl.textContent = display;
        }
    }

    /**
     * Show FQL error
     */
    function showFqlError(message) {
        const section = document.getElementById('fql-result-section');
        const resultEl = document.getElementById('fql-result');
        
        if (section && resultEl) {
            section.style.display = 'block';
            resultEl.className = 'fql-result error';
            resultEl.textContent = message;
        }
    }

    /**
     * Hide FQL result
     */
    function hideFqlResult() {
        const section = document.getElementById('fql-result-section');
        if (section) {
            section.style.display = 'none';
        }
    }

    /**
     * Show/hide FQL loading state
     */
    function showFqlLoading(loading) {
        const executeBtn = document.getElementById('fql-execute-btn');
        if (executeBtn) {
            executeBtn.disabled = loading;
            safeSetInnerHTML(executeBtn, loading 
                ? '<span class="btn-icon">⏳</span> Executing...'
                : '<span class="btn-icon">▶</span> Execute');
        }
    }

    /**
     * Load FQL history from API
     */
    async function loadFqlHistory() {
        const historyList = document.getElementById('fql-history-list');
        if (!historyList) return;
        
        try {
            if (typeof ExplorerAPI === 'undefined') {
                safeSetInnerHTML(historyList, '<div class="window-empty">API not available</div>');
                return;
            }
            
            if (!ExplorerAPI.isConfigured || !ExplorerAPI.isConfigured()) {
                safeSetInnerHTML(historyList, '<div class="window-empty">Select a workspace first</div>');
                return;
            }
            
            if (!ExplorerAPI.fetchCursorHistory) {
                safeSetInnerHTML(historyList, '<div class="window-empty">History not available</div>');
                return;
            }
            
            const history = await ExplorerAPI.fetchCursorHistory();
            
            if (!history || !history.statements || history.statements.length === 0) {
                safeSetInnerHTML(historyList, '<div class="window-empty">No statements yet</div>');
                return;
            }
            
            // Display history items (most recent first)
            const statements = history.statements.slice().reverse();
            safeSetInnerHTML(historyList, statements.slice(0, 50).map((stmt, idx) => `
                <div class="fql-history-item" data-statement="${escapeHtml(stmt)}" title="Click to use this query">
                    <span class="fql-history-index">${statements.length - idx}</span>
                    <span class="fql-history-text">${escapeHtml(truncate(stmt, 60))}</span>
                </div>
            `).join(''));
            
            // Add click handlers
            historyList.querySelectorAll('.fql-history-item').forEach(item => {
                item.addEventListener('click', function() {
                    const stmt = this.getAttribute('data-statement');
                    if (stmt) {
                        setFqlEditorText(stmt);
                        // Focus the editor
                        const editor = document.getElementById('fql-editor');
                        editor?.focus();
                    }
                });
            });
            
        } catch (error) {
            console.error('[ExplorerWindows] Failed to load FQL history:', error);
            safeSetInnerHTML(historyList, '<div class="window-empty">Failed to load history</div>');
        }
    }

    /**
     * Escape HTML for safe display
     */
    function escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }

    /**
     * Truncate text with ellipsis
     */
    function truncate(text, maxLength) {
        if (!text || text.length <= maxLength) return text;
        return text.substring(0, maxLength - 3) + '...';
    }

    // ============================================
    // Utilities
    // ============================================
    function debounce(func, wait) {
        let timeout;
        return function(...args) {
            clearTimeout(timeout);
            timeout = setTimeout(() => func.apply(this, args), wait);
        };
    }
    
    // ============================================
    // Check if any windows are open
    // ============================================
    function hasOpenWindows() {
        for (const windowId in windows) {
            if (windows[windowId] && isOpen(windowId)) {
                return true;
            }
        }
        return false;
    }
    
    // ============================================
    // Close all windows
    // ============================================
    function closeAll() {
        for (const windowId in windows) {
            if (windows[windowId] && isOpen(windowId)) {
                close(windowId);
            }
        }
    }

    // ============================================
    // Templates Window
    // ============================================
    let templatesCache = [];
    let selectedTemplateCategory = null;
    
    const TEMPLATE_CATEGORIES = [
        { id: 'all', name: 'All', icon: 'grid-3x3' },
        { id: 'slide_deck', name: 'Slides', icon: 'presentation' },
        { id: 'book', name: 'Docs', icon: 'book-open' },
        { id: 'podcast', name: 'Audio', icon: 'mic' },
        { id: 'video', name: 'Video', icon: 'video' },
        { id: 'dashboard', name: 'Dash', icon: 'layout-dashboard' },
        { id: 'website', name: 'Web', icon: 'globe' }
    ];
    
    function initTemplatesWindow(win) {
        // Search input
        const searchInput = win.querySelector('#templates-search-input');
        searchInput?.addEventListener('input', function() {
            filterTemplatesList(this.value);
        });
        
        // Render filter row
        renderTemplatesFilterRow();
        
        // Re-init lucide icons
        if (typeof lucide !== 'undefined') {
            lucide.createIcons();
        }
        
        // Load templates
        loadTemplates();
    }
    
    function renderTemplatesFilterRow() {
        const row = document.getElementById('templates-filter-row');
        if (!row) return;
        
        safeSetInnerHTML(row, TEMPLATE_CATEGORIES.map(cat => `
            <button class="templates-filter-btn ${!selectedTemplateCategory && cat.id === 'all' || selectedTemplateCategory === cat.id ? 'active' : ''}" 
                    data-category="${cat.id}" title="${cat.name}">
                <i data-lucide="${cat.icon}"></i>
            </button>
        `).join(''));
        
        // Bind click handlers
        row.querySelectorAll('.templates-filter-btn').forEach(btn => {
            btn.addEventListener('click', () => {
                selectedTemplateCategory = btn.dataset.category === 'all' ? null : btn.dataset.category;
                row.querySelectorAll('.templates-filter-btn').forEach(b => b.classList.remove('active'));
                btn.classList.add('active');
                filterTemplatesList(document.getElementById('templates-search-input')?.value || '');
            });
        });
        
        if (typeof lucide !== 'undefined') lucide.createIcons();
    }
    
    async function loadTemplates() {
        const list = document.getElementById('templates-list');
        if (!list) return;
        
        if (typeof ExplorerAPI === 'undefined' || !ExplorerAPI.isConfigured || !ExplorerAPI.isConfigured()) {
            // Use sample templates for demo
            templatesCache = getSampleTemplates();
            renderTemplatesList(templatesCache);
            return;
        }
        
        try {
            const config = ExplorerAPI.getConfig ? ExplorerAPI.getConfig() : {};
            const apiEndpoint = config.apiEndpoint || '';
            const workspaceId = config.workspaceId;
            
            if (!workspaceId) {
                templatesCache = getSampleTemplates();
                renderTemplatesList(templatesCache);
                return;
            }
            
            const url = `${apiEndpoint}/workspaces/${workspaceId}/templates/`;
            const headers = { 'Accept': 'application/json' };
            
            if (ExplorerAPI.getAuthHeaders) {
                Object.assign(headers, ExplorerAPI.getAuthHeaders());
            }
            
            const response = await fetch(url, { method: 'GET', headers, credentials: 'include' });
            
            if (response.ok) {
                templatesCache = await response.json();
                renderTemplatesList(templatesCache);
            } else {
                templatesCache = getSampleTemplates();
                renderTemplatesList(templatesCache);
            }
        } catch (err) {
            console.warn('[ExplorerWindows] Failed to load templates:', err);
            templatesCache = getSampleTemplates();
            renderTemplatesList(templatesCache);
        }
    }
    
    function getSampleTemplates() {
        return [
            { id: 'slide_deck_1', name: 'Business Pitch', description: 'Professional pitch deck', mixin_name: 'slide_deck', is_system: true },
            { id: 'slide_deck_2', name: 'Tech Overview', description: 'Technical documentation', mixin_name: 'slide_deck', is_system: true },
            { id: 'book_1', name: 'Research Report', description: 'Academic report format', mixin_name: 'book', is_system: true },
            { id: 'book_2', name: 'User Manual', description: 'Product documentation', mixin_name: 'book', is_system: true },
            { id: 'podcast_1', name: 'Interview', description: 'Interview podcast', mixin_name: 'podcast', is_system: true },
            { id: 'video_1', name: 'Explainer', description: 'Educational video', mixin_name: 'video', is_system: true },
            { id: 'dashboard_1', name: 'KPI Dashboard', description: 'Key metrics', mixin_name: 'dashboard', is_system: true },
            { id: 'website_1', name: 'Landing Page', description: 'Product page', mixin_name: 'website', is_system: true }
        ];
    }
    
    function renderTemplatesList(templates) {
        const list = document.getElementById('templates-list');
        if (!list) return;
        
        if (!templates || templates.length === 0) {
            safeSetInnerHTML(list, `
                <div class="window-empty">
                    <i data-lucide="file-x"></i>
                    <span>No templates available</span>
                </div>
            `);
            if (typeof lucide !== 'undefined') lucide.createIcons();
            return;
        }
        
        const categoryIcons = {
            'slide_deck': 'presentation',
            'book': 'book-open',
            'podcast': 'mic',
            'video': 'video',
            'dashboard': 'layout-dashboard',
            'website': 'globe',
            'custom': 'file-text'
        };
        
        safeSetInnerHTML(list, templates.map(t => {
            const icon = categoryIcons[t.mixin_name] || 'file-text';
            return `
                <div class="template-item" data-template-id="${escapeHtml(t.id)}" data-category="${escapeHtml(t.mixin_name)}">
                    <div class="template-info">
                        <i data-lucide="${icon}" class="template-icon ${t.mixin_name}"></i>
                        <div class="template-details">
                            <span class="template-name">${escapeHtml(t.name)}</span>
                            ${t.description ? `<span class="template-description">${escapeHtml(t.description)}</span>` : ''}
                        </div>
                    </div>
                    <div class="template-actions">
                        <button class="template-action-btn" data-action="use" title="Use this template">
                            <i data-lucide="plus"></i>
                        </button>
                    </div>
                </div>
            `;
        }).join(''));
        
        // Bind click handlers
        list.querySelectorAll('.template-item').forEach(item => {
            const templateId = item.dataset.templateId;
            
            item.addEventListener('click', () => {
                useTemplate(templateId);
            });
            
            item.querySelector('[data-action="use"]')?.addEventListener('click', (e) => {
                e.stopPropagation();
                useTemplate(templateId);
            });
        });
        
        if (typeof lucide !== 'undefined') lucide.createIcons();
    }
    
    function filterTemplatesList(searchTerm) {
        const list = document.getElementById('templates-list');
        if (!list) return;
        
        const items = list.querySelectorAll('.template-item');
        const term = searchTerm.toLowerCase();
        
        items.forEach(item => {
            const name = item.querySelector('.template-name')?.textContent.toLowerCase() || '';
            const desc = item.querySelector('.template-description')?.textContent.toLowerCase() || '';
            const category = item.dataset.category;
            
            const matchesSearch = !term || name.includes(term) || desc.includes(term);
            const matchesCategory = !selectedTemplateCategory || category === selectedTemplateCategory;
            
            item.style.display = (matchesSearch && matchesCategory) ? '' : 'none';
        });
    }
    
    function useTemplate(templateId) {
        const template = templatesCache.find(t => t.id === templateId);
        if (!template) {
            console.warn('[ExplorerWindows] Template not found:', templateId);
            return;
        }
        
        // Create artefact window via ExplorerTabs
        if (typeof ExplorerTabs !== 'undefined' && ExplorerTabs.createArtefactWindow) {
            ExplorerTabs.createArtefactWindow(template);
            // Switch to Artefacts tab
            ExplorerTabs.switchTab('artefacts');
            // Close templates window
            close('templates');
        } else {
            console.warn('[ExplorerWindows] ExplorerTabs.createArtefactWindow not available');
        }
    }

    // ============================================
    // Public API
    // ============================================
    /**
     * Hide all floating windows that belong to a specific tab.
     * Remembers which ones were open so they can be restored.
     * @param {string} tabId - 'graph' or 'bookmarks'
     */
    function hideWindowsForTab(tabId) {
        const windowIds = tabWindowMap[tabId];
        if (!windowIds) return;

        const wasOpen = [];
        windowIds.forEach(id => {
            const win = windows[id];
            if (win && win.classList.contains('visible')) {
                wasOpen.push(id);
                win.classList.remove('visible');
                win.classList.add('tab-hidden'); // mark as hidden-by-tab, not user-closed
            }
        });
        hiddenWindowState[tabId] = wasOpen;
        console.log(`[ExplorerWindows] Hidden windows for tab "${tabId}":`, wasOpen);
    }

    /**
     * Restore (show) floating windows that were previously hidden when leaving a tab.
     * @param {string} tabId - 'graph' or 'bookmarks'
     */
    function showWindowsForTab(tabId) {
        const windowIds = hiddenWindowState[tabId];
        if (!windowIds || windowIds.length === 0) return;

        windowIds.forEach(id => {
            const win = windows[id];
            if (win && win.classList.contains('tab-hidden')) {
                win.classList.remove('tab-hidden');
                win.classList.add('visible');
                updateToolbarButton(id, true);
            }
        });
        console.log(`[ExplorerWindows] Restored windows for tab "${tabId}":`, windowIds);
        // Clear state after restoring
        hiddenWindowState[tabId] = [];
    }

    global.ExplorerWindows = {
        init,
        reset,
        open,
        close,
        toggle,
        isOpen,
        hasOpenWindows,
        closeAll,
        showNodeDetails,
        addNodeToSelection,
        removeNodeFromSelection,
        clearDetails,
        getSelectedNodes,
        updateLegend,
        updateOperatorStates,
        loadFqlHistory,
        loadCheckpointsAndBookmarks,
        clearCheckpointsAndBookmarks,
        updateStateData,
        updateDocumentsData,
        loadPatterns,
        hideWindowsForTab,
        showWindowsForTab,
        // Shared z-index counter — call this to get the next value that sits above
        // all currently visible cards (both regular windows and task windows).
        raiseZIndex() {
            _winZCounter += 1;
            return _winZCounter;
        }
    };

})(window);
